<template>
  <div
      :class="[
      'w-full bg-white rounded-lg shadow-sm p-2 md:p-3 outline-none transition',
      'border-2',
      isActive
        ? 'border-indigo-500 ring-2 ring-indigo-300/60 bg-indigo-50/40'
        : 'border-gray-400 hover:border-gray-500 hover:shadow-md'
    ]"
      :tabindex="isActive ? 0 : -1"
      @click="$emit('requestFocus')"
  >
    <!-- 1) Meta -->
    <div class="flex items-center justify-between gap-3 min-h-7">
      <div class="text-[11px] text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-1">
        <span v-if="timestampText">{{ timestampText }}</span>

        <template v-if="authorName">
          <span v-if="timestampText">•</span>
          <span class="text-gray-800 font-semibold">{{ authorName }}</span>
        </template>

        <template v-if="message.channel">
          <span>•</span>
          <span
              class="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600"
              :title="`#${message.channel}`"
          >
            #{{ message.channel }}
          </span>
        </template>

        <template v-if="message.category">
          <span>•</span>
          <span
              class="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600"
              :title="message.category"
          >
            {{ message.category }}
          </span>
        </template>

        <template v-if="message.platform">
          <span>•</span>
          <span
              class="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-gray-600"
              :title="message.platform.toLowerCase()"
          >
            {{ message.platform.toLowerCase() }}
          </span>
        </template>

        <!-- Der Warteschlangenzustand. Bauart der Vektorisiert-Pille daneben:
             beide sagen etwas über die technische Verarbeitung, nicht über
             den Inhalt.

             ⚠️ Bewusst HIER in der Meta-Zeile und nicht in der Kriterienzeile:
             die steht unter v-if="hasCriteria", und ohne gewählte Session
             rendert die Liste 0 Kriterienpillen (gemessen: 0 auf 227 Karten).
             Dort wäre der Indikator genau dann unsichtbar, wenn niemand eine
             Session gewählt hat.

             ⚠️ DREI Zustände, und „wartet" ist nicht „in Karenz": eine
             Nachricht jünger als RATING_WORKER_MIN_AGE_SECONDS wird vom
             Worker noch gar nicht gesehen. „Wartet auf Bewertung" wäre dort
             schlicht falsch. Und steht der Worker still, wartet die Nachricht
             auf etwas, das niemand abholt — auch das ist ein eigener Satz. -->
        <template v-if="warteZustand">
          <span>•</span>
          <span
              class="px-2 py-0.5 rounded-full border text-[11px]"
              :class="warteZustand.klasse"
              :title="warteZustand.hinweis"
          >
            {{ warteZustand.text }}
          </span>
        </template>

        <!-- Der Vektorzustand. Bis zum 24.08. stand hier eine rohe
             Boolean-Pille aus `message.vectorized` — zwei Zustände für eine
             Auswahl mit vier.

             🔴 In einem Fall war sie FALSCH: die Selbstheilung des RAGWorkers
             greift bei `vectorized = true`, wenn sich die Bewertung nach dem
             Ingest geändert hat. Die Pille sagte dazu „Vektorisiert", während
             die Nachricht in Wahrheit auf einen neuen Lauf wartet. Gegen die
             echte Datenbank belegt (verification/probe_rag_queue.py, P3).

             ⚠️ Welcher Zweig gegriffen hat, weiß nur der Dienst — die Karte
             hat vectorized und vectorized_at, aber nicht max(updated_at) der
             Bewertungszeilen. Deshalb liefert die Auskunft drei Eimer.

             ⚠️ Ohne geladene Auskunft (`ragStatus === null`) fällt die Anzeige
             auf genau das Bestandsverhalten zurück. Sie verschwindet NICHT:
             `message.vectorized` steht in GET /messages und ist auch allein
             eine wahre Aussage — nur eine gröbere. -->
        <template v-if="vektorZustand">
          <span>•</span>
          <span
              class="px-2 py-0.5 rounded-full border text-[11px]"
              :class="vektorZustand.klasse"
              :title="vektorZustand.hinweis"
          >
            {{ vektorZustand.text }}
          </span>
        </template>

        <template v-if="message.referenced_message">
          <span>•</span>
          <button
              type="button"
              @click.stop="scrollToReferencedMessage"
              class="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 transition text-[11px] max-w-xs truncate"
              :title="`Antwort auf ${referencedMessageAuthor}: ${message.referenced_message.content}`"
          >
            ↩ {{ truncatedReferencedContent }}
          </button>
        </template>

        <!-- Wann bewertet wurde. Steht ganz hinten, weil davor die Angaben zur
             NACHRICHT stehen und hier die zur BEWERTUNG anfangen.
             "Geändert" erscheint nur, wenn wirklich nachtraeglich geschrieben
             wurde -- siehe ZEITSTEMPEL_TOLERANZ_MS, die Zahl ist am Bestand
             gemessen. -->
        <template v-if="bewertetText">
          <span>•</span>
          <BaseTooltip :text="bewertungsZeitTitel">
            <span class="border-b border-dotted border-gray-400">{{ bewertetText }}</span>
          </BaseTooltip>
        </template>

        <template v-if="geaendertText">
          <span>•</span>
          <BaseTooltip :text="bewertungsZeitTitel">
            <span class="border-b border-dotted border-gray-400">{{ geaendertText }}</span>
          </BaseTooltip>
        </template>
      </div>

      <div class="flex items-center gap-2 shrink-0">
        <!-- Merken. @click.stop ist PFLICHT: die Kartenwurzel trägt
             @click="$emit('requestFocus')", und ohne .stop würde jeder
             Merk-Klick nebenbei die Karte auswählen — also den Fokus
             verschieben, während jemand nur eine Nachricht vormerkt.

             Der Zustand steht im Modul, nicht in der Karte: gemerkt wird hier,
             gebraucht wird die Liste auf der Reflexionsseite. Ein Emit an die
             Liste wäre ein zweiter Weg zu demselben Zustand.

             Beschriftet ist der Knopf seit dem 18.08. nur noch mit dem Stern
             (Entscheidung Simon, weniger Text in der Kartenzeile). Damit
             trägt allein die Form die Aussage: gefüllt = gemerkt, offen =
             nicht gemerkt. Ohne sichtbaren Text braucht der Knopf ein
             aria-label — der title allein ist als zugänglicher Name
             browserabhängig. Größe und Polsterung bleiben unverändert:
             die Pille daneben ("n korrigiert") führt dieselben Klassen, ein
             größerer Stern schöbe die Zeile. -->
        <button
            type="button"
            @click.stop="merkenUmschalten"
            class="px-2 py-1 text-[0.65rem] rounded border transition"
            :class="
            istGemerkt
              ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
          "
            :title="
            istGemerkt
              ? `Gemerkt — auf der Reflexionsseite wird daraus ein Vergleichssatz. Klicken zum Vergessen. (${KUERZEL.merken})`
              : `Für einen Vergleichssatz merken. Die Liste steht auf der Reflexionsseite. (${KUERZEL.merken})`
          "
            :aria-label="
            istGemerkt
              ? `Gemerkt — klicken zum Vergessen (${KUERZEL.merken})`
              : `Für einen Vergleichssatz merken (${KUERZEL.merken})`
          "
        >
          {{ istGemerkt ? '★' : '☆' }}
        </button>

        <!-- Indigo als Gegenpol zum violetten KI-Badge: die Farbe der
             Menschenarbeit in dieser App. -->
        <span
            v-if="korrigierteAnzahl > 0"
            class="px-2 py-1 text-[0.65rem] rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
            :title="`${korrigierteAnzahl} von ${visibleCriteriaNormalized.length} Kriterien von Hand korrigiert`"
        >
          {{ korrigierteAnzahl }} korrigiert
        </span>

        <span
            v-if="!message.reviewed"
            class="inline-flex items-center gap-1 px-2 py-1 text-[0.65rem] rounded bg-violet-100 text-violet-700 cursor-help"
            title="Die Kriterienwerte sind Vorschläge des Modells, noch nicht geprüft."
        >
          <SparklesIcon class="w-3.5 h-3.5" />
          KI-Vorschlag
        </span>

        <span
            v-else
            class="px-2 py-1 text-[0.65rem] rounded bg-emerald-100 text-emerald-700"
        >
          Freigegeben
        </span>
      </div>
    </div>

    <!-- 2) Criteria row -->
    <div v-if="hasCriteria" class="mt-2">
      <div class="flex items-stretch -mx-3">
        <!-- ACHTUNG, der Vergleichsknopf steht seit dem 18.08. abends HIER und
             nicht mehr in der Aktionsleiste. Zwei Gruende, und der zweite
             ist der wichtigere: dort war die Zeile voll, waehrend neben
             dem Wort "Kriterien" die halbe 224-px-Spalte frei steht -- und
             der Knopf liegt jetzt UEBER seiner eigenen Flaeche. Klappt sie
             auf, schiebt sie nur, was unter ihr liegt; der Knopf bleibt
             unter dem Mauszeiger stehen. Genau dafuer war die Flaeche am
             18.08. nach unten gewandert, und das ist damit erledigt.

             ACHTUNG, er haengt damit ZUSAETZLICH an hasCriteria. Ohne
             gewaehlte Session rendert die Liste 0 Kriterienpillen
             (gemessen: 0 auf 227 Karten) -- dort ist auch dieser Knopf
             weg. Hingenommen: in diesem Zustand zeigt die Karte
             ueberhaupt nichts Bewertbares.

             ACHTUNG, sichtbar nur, wenn es einen alten Stand ueberhaupt
             gibt (im Bestand 7 von 227 Nachrichten). Die Liste kommt aus
             derselben Auskunft wie die Warteschlange (mit_verlauf) -- die
             Karte fragt dafuer nichts eigenes ab.

             ACHTUNG, der Kuerzelname des Knopfes "Neu bewerten" steht hier
             NICHT ausgeschrieben: test_rate_again_dialog schneidet ueber
             dessen ERSTES Vorkommen in der Datei, und alles oberhalb der
             Leiste lenkte den Ausschnitt auf einen fremden Tooltip. -->
        <div class="w-56 pl-3 pr-2 flex items-center gap-2 text-[11px] font-semibold text-gray-700">
          <span>Kriterien</span>

          <BaseTooltip
              v-if="hatVerlauf"
              text="Zeigt, was die letzte Neubewertung an dieser Nachricht geändert hat."
          >
            <BaseButton
                variant="neutral"
                size="sm"
                type="button"
                :aria-expanded="diffOffen ? 'true' : 'false'"
                :aria-controls="`diff-${message.id}`"
                @click.stop.prevent="toggleDiff"
            >
              {{ diffOffen ? "Diffs verbergen" : "Diffs anzeigen" }}
            </BaseButton>
          </BaseTooltip>
        </div>

        <div class="flex-1 pr-3">
          <div
              class="bg-gray-50 border border-gray-200 rounded-md px-2 py-1"
              :style="criteriaGridStyle"
          >
            <BaseTooltip
                v-for="(mc, index) in visibleCriteriaNormalized"
                :key="mc.criterion?.id ?? mc.criterion_id"
                wrapper-class="w-full"
                :text="pillenTooltip(mc)"
            >
            <button
                data-kriterium-pille
                type="button"
                class="w-full rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-indigo-500"
                :disabled="message.reviewed"
                @focus="focusedCriterionIndex = index"
                @click.stop="openCriterionEditor(mc, index)"
            >
              <span
                  class="criteria-pill w-full"
                  :class="[
                  scoreClass(mc.value),
                  istKorrigiert(mc) ? 'criteria-pill-corrected' : '',
                  index === focusedCriterionIndex ? 'ring-1 ring-indigo-400' : '',
                  message.reviewed ? 'opacity-60 cursor-not-allowed' : ''
                ]"
              >
                <span
                    class="criteria-pill-name font-semibold text-gray-800 tracking-wide"
                    :title="mc.criterion?.name || ''"
                >
                  {{
                    criterionAbbrevMap[mc.criterion?.id ?? mc.criterion_id] ||
                    fallbackAbbrev(mc.criterion?.name || '')
                  }}
                </span>

                <span class="criteria-pill-score">
                  {{ formatScore(mc.value) }}
                </span>
              </span>
            </button>
            </BaseTooltip>
          </div>

          <!-- ACHTUNG: hier wird das Tastenkuerzel bewusst NICHT eingesetzt.
               test_rate_again_dialog ankert auf dem ersten Vorkommen des
               Kuerzelnamens in dieser Datei und schneidet von dort rueckwaerts
               bis ':text='. Ein Einsatz oberhalb der Knopfleiste lenkte den
               Anker auf einen fremden Tooltip -- der Bestandstest wuerde rot,
               ohne dass am Quelltext etwas falsch waere. Das Kuerzel steht im
               Tooltip des Knopfes, und dort genuegt es. -->
          <p v-if="message.reviewed" class="mt-1 text-[0.7rem] text-gray-400">
            Diese Nachricht ist freigegeben. <b>Neu bewerten</b> — der Kreispfeil rechts in
            der Knopfleiste — öffnet sie wieder.
          </p>
        </div>
      </div>

      <!-- Der Vergleich mit dem letzten Bewertungsstand.

           ⚠️ Er steht UNTER dem Knopf, der ihn oeffnet, und ueber der
           gesamten Breite: der Knopf bleibt beim Aufklappen an seinem
           Platz, die Tabelle bekommt trotzdem die Spalte der Pillen mit.

           ⚠️ Die Marken sind border-l-*, NIE border-* : eine Farbe ohne
           Seitenangabe faerbt alle vier Raender und kollidiert mit der
           Trennlinie. Fallthrough- und Eigenklassen haben dieselbe
           Spezifitaet; dann entscheidet die Palettenreihenfolge im
           erzeugten Stylesheet, nicht das class-Attribut.

           ⚠️ DREI Spalten, und die vierte ("Befund") ist am 18.08. abends
           entfallen (Simon): sie schrieb als Wort hin, was der Vergleich
           der beiden Werte daneben ohnehin zeigt. Was man den Zahlen
           NICHT ansieht, bleibt: der Vorwert steht in Klammern im Wert,
           die Herkunft im Titel der Zelle.

           ⚠️ Die Marke "Korrektur verloren" am Kriterium ist am 24.08.
           entfallen (Simon: zu negativ). Die amber-Faerbung der Zeile
           bleibt -- und sie steht NICHT allein da (WCAG 1.4.1): eine
           verlorene Korrektur heisst per Definition, dass die ALTE Zeile
           score_source='human' trug, und diffWert schreibt dort "von
           Hand" aus. Der Satz steht damit in der Wertspalte statt am
           Kriterium. Faellt dieses Wort weg, traegt die Aussage wirklich
           nur noch die Farbe -- deshalb haengt daran ein eigener Test. -->
      <div v-if="diffOffen" :id="`diff-${message.id}`"
           class="mt-2 border-t border-gray-200 pt-2">
        <p v-if="diffFehler" class="text-xs text-red-700">{{ diffFehler }}</p>
        <p v-else-if="diffLaeuft" class="text-xs text-gray-500">wird geladen…</p>
        <template v-else-if="diff">
          <p v-if="diffUnklar" class="text-xs text-amber-800">
            ⚠️ Die Archivstände lassen sich nicht sicher trennen — ein Kriterium
            kommt in einem Stand doppelt vor. Es wird deshalb kein Vergleich
            gezeigt.
          </p>
          <template v-else-if="diff.vergleich">
            <p class="text-xs text-gray-600 mb-1">{{ diffKopfzeile }}</p>
            <table class="w-full text-[11px]">
              <thead>
                <tr class="text-gray-500 text-left">
                  <!-- Die Breite steht am Kriterium, damit die beiden Werte
                       NEBENEINANDER lesbar bleiben. Ohne sie verteilt der
                       Browser nach Inhalt, und die kurzen Zahlen rutschen an
                       den rechten Rand -- der Vergleich, um den es geht,
                       liegt dann eine halbe Kartenbreite auseinander. -->
                  <th class="py-0.5 pr-2 w-1/2">Kriterium</th>
                  <th class="py-0.5 pr-2">vorher</th>
                  <th class="py-0.5 pr-2">nachher</th>
                </tr>
              </thead>
              <tbody>
                <tr
                    v-for="z in diff.vergleich.zeilen"
                    :key="z.criterion_id"
                    class="border-l-2"
                    :class="diffZeileKlasse(z)"
                >
                  <!-- Der Abstand sitzt am ersten td und NICHT als pl-1 am tr:
                       padding gilt fuer display: table-row nicht, die Marke
                       klebte damit direkt am Text. -->
                  <td class="py-0.5 pr-2 pl-1">
                    {{ z.name || z.criterion_id }}
                  </td>
                  <td class="py-0.5 pr-2 font-mono" :title="diffHerkunft(z.alt)">{{ diffWert(z.alt) }}</td>
                  <td class="py-0.5 pr-2 font-mono" :title="diffHerkunft(z.neu)">{{ diffWert(z.neu) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
          <p v-else class="text-xs text-gray-500">
            Es gibt noch keinen neueren Stand zum Vergleichen.
          </p>
        </template>
      </div>
    </div>

    <!-- 3) Message + Actions -->
    <div class="mt-2 flex items-center gap-3">
      <!-- ACHTUNG: die Hoehe steht in messageTextPaneStyle, NICHT als max-h-16
           in der Klassenliste. Zwei Quellen fuer dieselbe Groesse waeren eine zu
           viel -- ein inline-Style gewinnt gegen jede Klasse, und die Klasse
           stuende dann als tote, aber plausibel aussehende Angabe daneben. -->
      <div
          ref="messageTextPane"
          class="flex-1 min-w-0 border rounded-md px-2 py-1 bg-gray-50 text-gray-800 overflow-y-auto"
          :style="messageTextPaneStyle"
      >
        <p class="text-xs md:text-sm whitespace-pre-wrap leading-snug">
          {{ message.content }}
        </p>
      </div>

      <div class="flex items-center gap-2 text-[11px] text-gray-500 shrink-0">
        <a
            v-if="message.link"
            :href="normalizedLink"
            target="_blank"
            rel="noopener"
            class="text-indigo-600 hover:underline"
            @click.stop
        >
          Zur Nachricht
        </a>

        <!-- ACHTUNG: Farbe und Groesse gehoeren in variant/size, NICHT in ein
             class-Attribut. Fallthrough-Klassen und die Eigenklassen von
             BaseButton haben dieselbe Spezifitaet; dann entscheidet die
             Reihenfolge im erzeugten Tailwind-Stylesheet, und dort steht emerald
             hinter amber, green und red. Genau deshalb sahen diese drei Knoepfe
             bis hierher identisch gruen aus. -->
        <!-- "Neu bewerten" haengt an KEINER Bedingung mehr, und das ist der
             fachliche Kern: der RatingWorker waehlt ueber
             coalesce(rated_count,0) < total_criterias, NICHT ueber reviewed.
             Nach einem Bot-/edit bleiben nur menschliche Zeilen stehen
             (reviewed=False, rated_count == total_criterias) -- der Worker
             greift dann nie wieder, und ausgerechnet dort war dieser Knopf
             bisher unsichtbar, weil er an reviewed===true haengte.
             ACHTUNG: der Tooltip nennt seit dem 18.08. nur noch Handlung und
             Folge (Entscheidung Simon, kurz und praezise). Die Auswahlregel
             des Workers steht damit nur noch hier im Quelltext -- sie aendert
             die Entscheidung des Korrektors nicht, und ein Tooltip, der vier
             Zeilen lang ist, wird nicht gelesen.
             ACHTUNG: NICHT disabled schalten. An einem disabled-Knopf erscheint
             der Tooltip NIE (Chrome und Firefox unterdruecken dort jedes
             Mausereignis) -- also genau dann nicht, wenn jemand ihn braucht.
             ACHTUNG: kein sichtbarer Text mehr, deshalb ist aria-label PFLICHT.
             Das SVG von heroicons traegt selbst aria-hidden="true"; ohne Label
             heisst der Knopf fuer ein Hilfsmittel nur "Schaltflaeche".
             'block' am Icon ist keine Kosmetik: als inline-Element richtet sich
             das SVG an der Grundlinie aus, die Zeilenbox waechst um die
             Unterlaenge und die Leiste wird hoeher. -->
        <BaseTooltip
            :text="`${KUERZEL.neuBewerten} · ${hinweisText(NEU_BEWERTEN)}`"
        >
          <BaseButton
              variant="neutral"
              size="sm"
              type="button"
              aria-label="Neu bewerten"
              @click.stop.prevent="onRateAgain"
          >
            <ArrowPathIcon class="w-4 h-4 block" />
          </BaseButton>
        </BaseTooltip>

        <!-- Dieser Trenner traegt DIESELBE Bedingung wie der Knopf dahinter.
             Ohne sie stuende er bei einer freigegebenen Nachricht als zweite
             Haarlinie direkt neben der vor "Loeschen" -- ein Fehler, den in
             einer Liste mit 200+ Karten niemand meldet. -->
        <span v-if="!message.reviewed" class="w-px h-5 bg-gray-300" aria-hidden="true"></span>

        <BaseTooltip
            v-if="!message.reviewed"
            :text="`${KUERZEL.freigeben} · Bewertung abschließen. Die Nachricht wird danach neu vektorisiert.`"
        >
          <BaseButton
              variant="primary"
              size="sm"
              type="button"
              @click.stop="handleApprove"
          >
            Freigeben
          </BaseButton>
        </BaseTooltip>

        <!-- Die zerstoerende Aktion steht abgesetzt hinter einem Trenner, damit
             sie nicht als dritte gleichrangige Wahl gelesen wird. -->
        <span class="w-px h-5 bg-gray-300" aria-hidden="true"></span>

        <BaseTooltip text="Nachricht endgültig löschen, mit allen Bewertungen">
          <BaseButton
              variant="delete"
              size="sm"
              type="button"
              @click.stop.prevent="onDelete"
          >
            Löschen
          </BaseButton>
        </BaseTooltip>
      </div>
    </div>

    <!-- 3b) Gesprächskontext -->
    <!-- Warum das hier steht: Kriterien wie "Fachliches Eingehen (auf
         Vorredner)" und "Adressatengerecht" sind ohne die Vorgängernachrichten
         nicht beurteilbar. Das Modell bekommt dieses Fenster seit Paket B2 in
         den Prompt; der Korrektor sah davon bisher nur die einzeilige
         Reply-Zeile oben — und die haben im Bestand genau vier von 216
         Nachrichten. Bei den 200 Echtdaten-Nachrichten sah er nichts. -->
    <!-- ACHTUNG: kein Aufklapper mehr. Der Verlauf ist fuer Kriterien wie
         "Fachliches Eingehen" die Beurteilungsgrundlage -- wer ihn erst
         aufklappen muss, bewertet beim ersten Durchgang ohne ihn. Er steht
         deshalb seit dem 08.08. AUSGEKLAPPT da, sobald die Karte die aktive
         ist; die Automatik davor band ihn an den offenen Kriterien-Editor und
         zeigte ihn damit erst, wenn die Bewertung schon begonnen hatte.
         Gezeigt wird er fuer die AKTIVE Karte, und die laedt ihn selbst. An die
         ganze Liste gehaengt waeren es 400+ Abfragen; so ist es genau eine je
         Nachricht, die tatsaechlich bearbeitet wird. -->
    <div v-if="isActive" class="mt-1">
      <div class="p-2 bg-gray-50 border border-gray-200 rounded-lg text-[11px]">
        <p v-if="kontextLaedt" class="text-gray-500">Verlauf wird geladen …</p>

        <p v-else-if="kontextFehler" class="text-red-700">
          {{ kontextFehler }}
        </p>

        <template v-else-if="kontext">
          <!-- Eine Zeile statt dreier Absaetze. Was hier steht, muss beim
               Bewerten in einem Blick lesbar sein.
               ACHTUNG: die Kopfzeile traegt nur noch Verlaufsschalter und
               Quelle. Die Beleglage stand hier bis zum 17.08. als Pille mit
               vier Zustaenden ("Anzahl belegt" / "weicht vom Lauf ab" /
               "Beleg widerspruechlich" / "ohne Beleg") und ist entfernt: sie
               beantwortet eine Frage, die beim Bewerten niemand stellt --
               naemlich ob rating_meta.conversation_context_count des damaligen
               Laufs zur heute rekonstruierten Anzahl passt --, sah aber wie
               eine Aussage ueber die NACHRICHT aus. Geblieben ist der
               ausgeschriebene Satz fuer die zwei Faelle, in denen die Anzeige
               in die Irre fuehren KANN (siehe unten). -->
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
            <button
                type="button"
                class="font-semibold text-gray-700 hover:text-gray-900 rounded
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                :aria-expanded="kontextEntriesSichtbar ? 'true' : 'false'"
                @click.stop.prevent="kontextManuell = !kontextEntriesSichtbar"
            >
              <span class="inline-block w-3">{{ kontextEntriesSichtbar ? '▾' : '▸' }}</span>
              Verlauf
            </button>

            <BaseTooltip :text="kontextHerkunft">
              <span class="text-gray-500 border-b border-dotted border-gray-400">
                {{ kontextQuelleKurz }}
              </span>
            </BaseTooltip>

            <!-- Der Relevanzlauf (18.08.). Ein ZUSAETZLICHER LLM-Aufruf,
                 der je Verlaufszeile urteilt, ob sie zum selben
                 Kommunikationsstrang gehört. Er ändert am Bewertungsprompt
                 nichts — erhoben wird, nicht gefiltert.
                 ⚠️ Bewusst KEIN variant="warning": Weiß auf amber-500 sind
                 2,15:1, WCAG 1.4.11 verlangt für grafische Objekte 3:1.
                 Am 18.08. schon einmal gemessen und korrigiert. -->
            <button
                type="button"
                class="ml-auto shrink-0 px-1.5 py-0.5 rounded border border-gray-300
                       bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50
                       disabled:cursor-not-allowed
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                :disabled="relevanzLaeuft || !kontext.entries.length"
                :title="kontext.entries.length ? '' : relevanzLauf.ohneVerlauf"
                @click.stop.prevent="pruefeRelevanz"
            >
              {{ relevanzKnopfText }}
            </button>
          </div>

          <!-- WANN und WOMIT zuletzt erhoben wurde (19.08.). Seit der
               Hintergrundlauf existiert, entstehen Urteile auch ohne Klick —
               ohne diese Zeile wüsste niemand, ob ein „?" bedeutet „noch
               nichts gelaufen" oder „übersprungen".
               ⚠️ Beide Angaben kommen aus der Antwort des Kontextendpunkts
               und lagen dort schon vor diesem Umbau; gerendert wurden sie
               nie. Sie sagen NICHT, ob der Worker eingeschaltet ist. -->
          <p class="mt-1 text-gray-500">
            <template v-if="kontext.relevance_ran_at">
              {{ relevanzLauf.erhoben }} {{ zeitpunktText(kontext.relevance_ran_at) }}<template
                  v-if="kontext.relevance_model"> ({{ kontext.relevance_model }})</template>
            </template>
            <template v-else>{{ relevanzLauf.nichtErhoben }}</template>
          </p>

          <!-- Was der Lauf ergeben hat. ⚠️ Ein Modell, das zwei von fünf
               Zeilen überspringt, liefert ein Ergebnis, das wie ein
               vollständiges aussieht — der Unterschied steht nur hier. -->
          <p v-if="relevanzFehler" class="mt-1 text-red-700">{{ relevanzFehler }}</p>
          <p v-else-if="relevanzHinweis" class="mt-1 text-gray-600 italic">
            {{ relevanzHinweis }}
          </p>

          <!-- Laut, weil die Anzeige hier in die Irre fuehren KANN. -->
          <p v-if="kontextBelegLaut" class="mt-1" :class="kontextBeleg.klasse">
            {{ kontextBeleg.text }}
          </p>

          <!-- ACHTUNG: Zur Saettigung des Kandidatenfensters steht hier bewusst
               KEINE sichtbare Zeile mehr (Simon, 08.08.). Sie aendert am
               Bewerten nichts: der Prompt bekommt so oder so hoechstens k
               Vorgaenger, und jenseits des Fensters koennte sich allenfalls die
               AUSWAHL verschieben -- wofuer es eine id/Zeit-Vertauschung von
               ueber `width` Plaetzen braeuchte, waehrend der nebenlaeufige
               Ingest laut conversation_context.py bei rund 3 liegt.
               Ueber den Bestand gemessen erschien der Hinweis auf 92 % der
               Nachrichten. Die Angabe selbst ist nicht verschwunden: sie steht
               im Tooltip der Quellzeile (kontextHerkunft), zusammen mit Quelle
               und k. -->
          <p v-if="kontextGrund" class="mt-1 text-gray-600 italic">
            {{ kontextGrund }}
          </p>

          <!-- ⚠️ Die Aufteilung wird hier festgelegt und ist eine Zusage an das
               kommende Relevanz-Feature: RAND = HERKUNFT, FLÄCHE = RELEVANZ.
               Ohne diese Trennung säßen später zwei verschiedene Aussagen auf
               demselben Pixel, und eine davon gewänne zufällig.
               Der Rand ist links und farbig statt ein weiteres Element in der
               Zeile: eine vierte Spalte kostete Breite, und die Herkunft ist
               für alle Einträge einer Liste meist dieselbe — sie als Text zu
               wiederholen hieße, denselben Wert bis zu fünfmal zu lesen. -->
          <ol v-if="kontext.entries.length && kontextEntriesSichtbar" class="mt-2 space-y-1">
            <li
                v-for="(e, i) in kontext.entries"
                :key="e.id != null ? e.id : `i${i}`"
                class="flex gap-2 border-t border-t-gray-200 pt-1 pl-2 border-l-2"
                :class="[kontextHerkunftKlasse(e.origin), kontextRelevanzKlasse(e)]"
                :title="kontextHerkunftTitel(e.origin)"
            >
              <!-- Gezeigt wird der Name, unter dem derselbe Mensch auch in der
                   Meta-Zeile dieser Karte steht. Die Kennung des Prompts
                   ('author'/'P1') haengt im Tooltip -- sie ist der Wert, den
                   das Modell sieht, und sie darf nicht verschwinden: sonst
                   liesse sich ein Prompt-Log nicht mehr gegen die Anzeige
                   halten. Faellt display aus (Zeile ohne user_id, geloeschter
                   Nutzer), steht wieder die Kennung da, nie ein Platzhalter. -->
              <span
                  class="shrink-0 px-1.5 py-0.5 rounded font-semibold"
                  :class="e.author === 'author'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'bg-gray-100 text-gray-600'"
                  :title="kontextSprecherTitel(e)"
              >
                {{ e.display || e.author }}
              </span>
              <span class="shrink-0 text-gray-400 tabular-nums">{{ kontextZeit(e.ts) }}</span>
              <span class="min-w-0 whitespace-pre-wrap text-gray-800">{{ e.text }}</span>

              <!-- Der Schalter trägt die Aussage als TEXT und ist zugleich
                   die Korrektur.
                   ⚠️ Farbe darf nicht die einzige Information sein
                   (WCAG 1.4.1) — die Fläche allein wäre für jeden
                   unlesbar, der sie nicht unterscheiden kann.
                   ⚠️ Und der Klick sitzt HIER und nicht auf der Zeile: ein
                   Klick auf den Text kollidierte mit dem Markieren.
                   ⚠️ Eine Zeile ohne id bekommt keinen Schalter — es gäbe
                   kein Paar, an dem ein Urteil hängen könnte. Das steht als
                   Titel daran, statt still zu fehlen.
                   ⚠️ Die Breite ist FEST (SCHALTER_BREITE) und der Text
                   bricht nicht um: „?", „relevant" und „nicht relevant"
                   sind verschieden breit, und da die Spalte shrink-0 ist,
                   verschöbe jeder Klick den Umbruch des Nachrichtentextes
                   daneben. Ein Urteil zu ändern darf den Satzspiegel nicht
                   ändern.
                   ⚠️ `self-start` ist die andere Hälfte davon, und zwar für
                   die HÖHE. Ein Flex-Kind wird ohne es auf die volle
                   Zeilenhöhe gestreckt (`align-items: stretch`) — bei einem
                   dreizeiligen Verlaufstext ist der Knopf dann 82,5 statt
                   18,5 px hoch, gemessen, und er ändert seine Höhe mit jeder
                   Zeile, die der Text mehr oder weniger braucht.
                   ⚠️ Es gibt KEINEN Knopf, der die Korrektur zurücknimmt
                   (Simon, 19.08.). Der Schalter kennt zwei Werte und
                   schaltet zwischen ihnen; „wieder Modellstand" ist eine
                   dritte Sache, die niemand am Einzelfall braucht. Das
                   Modellurteil bleibt ohnehin unangetastet — es steht in
                   model_value und im Titel. -->
              <button
                  type="button"
                  class="ml-auto shrink-0 self-start text-center whitespace-nowrap
                         px-1 rounded border border-gray-300 bg-white text-gray-700
                         hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  :class="relevanzSchalterBreite"
                  :disabled="e.id == null"
                  :title="kontextRelevanzTitel(e)"
                  @click.stop.prevent="schalteRelevanz(e)"
              >
                {{ kontextRelevanzText(e) }}
              </button>
            </li>
          </ol>
        </template>
      </div>
    </div>

    <!-- 4) Editor -->
    <div v-if="hasCriteria && activeCriterion" class="criterion-editor mt-2">
      <div class="criterion-editor-header">
        <span class="criterion-editor-title">
          {{ activeCriterion.criterion.name }}
        </span>
        <button type="button" class="criterion-editor-close" @click="closeEditor">
          ✕
        </button>
      </div>

      <div class="mt-2 space-y-3">
        <!-- Der Wert wird ausschliesslich ueber die Schnellwahl gesetzt. Das
             frueher danebenstehende type="number"-Feld ist entfallen: es war ein
             zweiter Eingabeweg fuer denselben Wert, und es kannte keine Grenzen.
             Massgeblich ist die Skala -3..+3 -- die schreibt der aktive Prompt
             vor, und der Korrektur-Endpunkt prueft dasselbe Band. -->
        <!-- Die Schnellwahl steht mittig und in einer eigenen Zeile. Stuende der
             Modellvorschlag daneben, waere die Reihe um dessen halbe Breite nach
             links versetzt -- zentriert waere dann die Gruppe, nicht die Tasten. -->
        <div class="flex justify-center">
          <RatingPicker
              :modelValue="editValue"
              @update:modelValue="onRatingPickerUpdate"
          />
        </div>

        <!-- Der Modellvorschlag steht unter der Schnellwahl, nicht in der
             Pillenreihe: dort sind zehn Spalten a minmax(44px,1fr), ein
             zweiter Wert passt nicht hinein. -->
        <div v-if="modellHinweis" class="flex justify-center -mt-1">
          <span
              :class="modellHinweis.klasse"
              :title="modellHinweis.titel"
          >
            {{ modellHinweis.text }}
          </span>
        </div>

        <textarea
            ref="commentTextarea"
            class="w-full px-2 py-1 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            rows="2"
            placeholder="Kommentar (optional) — Taste C springt hierher"
            v-model="editComment"
        ></textarea>

        <div class="flex items-center justify-end gap-2 mt-1">
          <p v-if="hasUnsavedChanges" class="mr-auto text-[0.7rem] text-amber-600">
            Ungespeicherte Änderungen.
          </p>

          <BaseTooltip :text="`${KUERZEL.verwerfen} · Änderungen verwerfen und schließen`">
            <BaseButton variant="neutral" type="button" @click="discardChanges">
              Verwerfen
            </BaseButton>
          </BaseTooltip>
          <BaseTooltip :text="`${KUERZEL.speichern} · Speichern und schließen`">
            <BaseButton variant="switch" type="button" @click="saveAndClose">
              Speichern
            </BaseButton>
          </BaseTooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseButton from "@/BaseComponents/BaseButton.vue";
import BaseTooltip from "@/BaseComponents/BaseTooltip.vue";
import RatingPicker from "./RatingPicker.vue";
import { KUERZEL } from "@/async-sessions/shortcuts";
import { abonnieren, istGemerkt, speicherFehler, umschalten, MERK_MAX } from "@/async-sessions/merkliste";
import { NEU_BEWERTEN, hinweisText } from "@/async-sessions/hinweise";
import {
  VEKTOR,
  VEKTOR_KLASSE,
  vektorHinweis,
} from "@/async-sessions/vektorStatus";
import {
  BEFUND,
  LAUF as RELEVANZ_LAUF,
  RELEVANZ,
  SCHALTER_BREITE,
  ohneTitel,
  relevanzZustand,
} from "@/async-sessions/kontextRelevanz";
import { ArrowPathIcon, SparklesIcon } from "@heroicons/vue/24/outline";
import {
  getConversationContext,
  getRatingHistory,
  setContextRelevance,
  startContextRelevance,
} from "@/async-sessions/api/messages";

/**
 * Kontexte über das Neumounten der Karte hinweg festhalten, nach message.id.
 *
 * ACHTUNG, das ist kein Optimierungsluxus, sondern nötig: MessagesList.vue
 * setzt :key="m.id + '-' + criteriaWindowStart". Jedes Blättern im
 * Kriterienfenster erzeugt damit eine NEUE Komponenteninstanz, und alles in
 * data() ist weg. Ohne diesen Speicher schlösse sich ein geöffneter
 * Kontextblock beim Blättern und müsste neu geladen werden.
 *
 * Bewusst modulweit und nicht reaktiv: die Karte kopiert beim Anlegen in ihr
 * eigenes data(), und nur die Kopie treibt das Rendern. Ein reaktiver Store
 * wäre hier das erste seiner Art in diesem Repo — dafür ist der Anlass zu klein.
 *
 * Der Speicher lebt bis zum Seiten-Neuladen. Das ist richtig so: der Kontext
 * ist ein Lesebild, und der Bestand ändert sich während einer Sitzung nicht
 * von allein. Nach "Rate again" ist er trotzdem korrekt — das Fenster hängt an
 * den Nachrichten, nicht an den Bewertungen. Nur der BELEG kann sich dadurch
 * ändern (die Zellen werden gelöscht); wer das sehen will, lädt die Seite neu.
 */
const kontextSpeicher = new Map();

/**
 * Die Bewertungsskala.
 *
 * Seit dem Wegfall des freien Zahlenfelds ist die Schnellwahl der einzige
 * Eingabeweg, und sie kennt genau diese sieben Werte. Alles, was einen Wert
 * setzt (Pfeiltasten, Ziffern), muss deshalb hierauf klemmen -- ein Wert
 * ausserhalb waere gesetzt, aber nicht mehr darstellbar und nicht mehr
 * korrigierbar.
 *
 * Die Grenzen sind keine Erfindung der Oberflaeche: der aktive Prompt schreibt
 * score zwischen -3 und 3 vor, und der Korrektur-Endpunkt prueft dasselbe Band.
 *
 * ACHTUNG: BESTEHENDE Werte werden NICHT geklemmt. Oeffnet der Editor eine
 * Zelle mit einem Altwert ausserhalb der Skala, bleibt der stehen, bis jemand
 * ihn bewusst aendert. Stilles Umschreiben von Bestandsdaten beim blossen
 * Hinsehen waere schlimmer als ein Wert, den die Schnellwahl nicht hervorhebt.
 */
const WERT_MIN = -3;
const WERT_MAX = 3;

/**
 * Die zwei Hoehen des Nachrichtenfensters.
 *
 * KOMPAKT (4rem, das bisherige max-h-16) traegt rund drei Zeilen. Das ist fuer
 * das Durchblaettern der Liste richtig -- 216 Karten mit voller Nachrichtenlaenge
 * waeren nicht ueberblickbar.
 *
 * Sobald aber BEWERTET wird, ist genau der abgeschnittene Rest die
 * Beurteilungsgrundlage: wer nur die ersten drei Zeilen sieht, vergibt einen
 * Wert fuer einen Text, den er nicht gelesen hat. Der Scrollbalken war da, aber
 * er ist beim Bewerten der falsche Weg -- die Hand liegt auf den Zifferntasten,
 * nicht am Rad.
 *
 * ACHTUNG, warum das GEDECKELT waechst und nicht auf max-height:none geht:
 * unter dem Textfeld stehen Verlauf, Schnellwahl und Kommentarfeld. Eine
 * ungedeckelte Nachricht schoebe die Schnellwahl bei langen Texten aus dem
 * Sichtfeld -- die Anzeige waere dann vollstaendig und die Bedienung weg.
 * 45vh laesst der unteren Haelfte des Fensters Platz; laengere Nachrichten
 * bleiben ab dort scrollbar (Taste fuer scrollTextPane unveraendert).
 */
const TEXT_HOEHE_KOMPAKT = "4rem";
const TEXT_HOEHE_BEWERTUNG = "min(28rem, 45vh)";

/**
 * Ab welchem Abstand zwischen created_at und updated_at eine Bewertungszeile
 * als nachtraeglich GESCHRIEBEN gilt.
 *
 * Die Zahl ist gemessen, nicht gegriffen (17.08., 4540 Zeilen im Bestand):
 *
 *   Abstand < 1 ms      1190 Zeilen   Anlage-Rauschen, kein Schreibvorgang
 *   1 ms .. 1 min          0 Zeilen   nichts dazwischen
 *   > 1 min               42 Zeilen   die echten Aenderungen
 *
 * Die 1190 entstehen bei der ANLAGE: created_at und updated_at haben getrennte
 * ORM-Defaults, und zwischen den beiden datetime.now()-Aufrufen vergehen rund
 * 0,1 ms. Ein Vergleich auf Ungleichheit haette an all diesen Zeilen
 * "geaendert" behauptet, wo nie jemand etwas geaendert hat.
 *
 * Eine Sekunde liegt drei Groessenordnungen ueber dem Rauschen und eine unter
 * der kleinsten echten Aenderung -- die Luecke dazwischen ist leer, es gibt
 * hier also keinen Grenzfall zu entscheiden.
 */
const ZEITSTEMPEL_TOLERANZ_MS = 1000;

export default {
  // ACHTUNG: eine fehlende Registrierung ist STILL. Vue rendert dann nichts und
  // warnt nur in der Konsole; der Build laeuft durch, und der Knopf ist ein
  // leeres Kaestchen. Deshalb prueft test_rate_again_button beides.
  components: { ArrowPathIcon, BaseButton, BaseTooltip, RatingPicker, SparklesIcon },

  props: {
    message: { type: Object, required: true },
    allCriterias: { type: Array, default: () => [] },
    visibleCriterias: { type: Array, default: () => [] },
    isActive: { type: Boolean, default: false },
    onScrollToMessage: { type: Function, default: null },
    // Solange ein Dialog offen ist, haelt die Karte die Tastatur still.
    // ACHTUNG: das darf NICHT ueber isActive geloest werden -- isActive steuert
    // auch tabindex und die Hervorhebung.
    suspendKeys: { type: Boolean, default: false },
    // Der Warteschlangenstand, wie der Dienst ihn meldet -- als Ganzes und
    // nicht vorverdaut. Die Karte entscheidet daraus ihren eigenen Zustand;
    // die Liste soll ihn nicht 227 Mal einzeln ausrechnen.
    //
    // ACHTUNG, null heisst NICHT 'niemand wartet', sondern 'unbekannt'.
    // Ohne die Unterscheidung behauptete die Karte beim ersten Rendern
    // etwas ueber einen Zustand, den sie noch nicht kennt.
    ratingStatus: { type: Object, default: null },
    // Der Zwilling für die Vektorisierung. `null` heißt „unbekannt" und NICHT
    // „alles vektorisiert" — die Karte fällt dann auf `message.vectorized`
    // zurück, also auf das Bestandsverhalten.
    ragStatus: { type: Object, default: null },
  },

  emits: [
    "approve",
    // Freigeben bei ungespeicherten Aenderungen: der Parent fragt nach, was
    // damit geschehen soll, und fuehrt es ueber messageRefs aus.
    "approveBlocked",
    "rate",
    "delete",
    "rateAgain",
    "requestFocus",
    // Fenstergrenze beim Durchtabben: criteriaWindowStart gehoert dem Parent,
    // die Karte stellt nur einen Antrag. Sonst gaebe es zwei Wahrheiten.
    "requestCriteriaPage",
    // Fuer den Fokus-Modus: der Parent kann nicht sehen, ob ein Editor offen ist.
    "editorState",
    // Meldungen ohne Entscheidung laufen ueber den Hinweisstreifen der Liste,
    // nicht mehr ueber alert().
    "notice",
  ],

  data() {
    return {
      // Der Bewertungs-Diff. Wird erst beim Aufklappen geholt: an 227 Karten
      // gleichzeitig zu laden waere 227 Abfragen fuer eine Auskunft, die
      // fast niemand aufruft.
      diffOffen: false,
      diffLaeuft: false,
      diffFehler: "",
      diff: null,
      // Nicht reaktiv gemeint, nur im Template erreichbar: die Kuerzel stehen
      // gebuendelt in shortcuts.js, damit Tooltip und Info-Popover nicht
      // auseinanderlaufen koennen.
      KUERZEL,
      // Aus demselben Grund hier: was der Knopf ZUSAGT, steht in hinweise.js.
      // ACHTUNG, ohne diese beiden Zeilen ist der Tooltip im Template
      // undefined -- und zwar STILL, ohne Konsolenmeldung. Genau dafuer gibt
      // es in test_rate_again_dialog.py die Bindungspruefung.
      NEU_BEWERTEN,
      hinweisText,
      // Ebenso durchgereicht: die drei Zustände der Relevanzanzeige und
      // die Sätze am Lauf stehen in kontextRelevanz.js.
      // ⚠️ Was data() nicht durchreicht, ist im Template undefined — der
      // Schalter erschiene dann ohne Beschriftung, ohne Konsolenmeldung.
      // Bei der Breite wäre es schlimmer: eine undefinierte Klasse fällt
      // gar nicht auf, der Schalter wäre nur wieder verschieden breit.
      RELEVANZ,
      relevanzLauf: RELEVANZ_LAUF,
      relevanzBefund: BEFUND,
      relevanzSchalterBreite: SCHALTER_BREITE,

      // Zustand des Relevanzlaufs dieser Karte.
      relevanzLaeuft: false,
      relevanzFehler: null,
      relevanzBericht: null,

      activeCriterionId: null,
      editValue: null,
      editComment: "",
      focusedCriterionIndex: 0,
      originalValue: null,
      originalComment: "",
      // null = der Automatik folgen (auf, sobald bewertet wird)
      kontextManuell: null,
      kontextLaedt: false,
      kontextFehler: null,
      kontext: null,
      // Ob diese Nachricht gemerkt ist. Gehalten wird der Zustand im Modul
      // (localStorage); hier steht nur die Spiegelung fuer die Anzeige, und sie
      // wird ueber das Abonnement nachgezogen -- sonst blieben zwei Karten
      // derselben Nachricht in verschiedenen Filtern verschiedener Meinung.
      istGemerkt: false,
      merkAbbestellen: null,
    };
  },

  computed: {
    /**
     * Was zur Warteschlange dieser Nachricht zu sagen ist — oder null.
     *
     * null heißt: nichts anzeigen. Das ist der Normalfall (die Nachricht ist
     * vollständig bewertet) UND der Fall „Stand unbekannt" — beide dürfen
     * keine Pille erzeugen, denn eine Pille ist eine Behauptung.
     *
     * ⚠️ Die drei Zustände sind nicht drei Formulierungen desselben:
     *
     *   wartet     Der Worker sieht die Nachricht und kommt zu ihr. Bei einem
     *              Stapel dauert das: er arbeitet seriell, gemessen 70 s je
     *              Nachricht im Median.
     *   in Karenz  Jünger als RATING_WORKER_MIN_AGE_SECONDS — der Worker sieht
     *              sie NOCH NICHT. „Wartet" wäre hier falsch.
     *   Worker aus Der Schalter steht auf 1. Dann wartet die Nachricht auf
     *              etwas, das niemand abholt, und das gehört gesagt.
     *
     * Es gibt bewusst KEIN „wird gerade bewertet": dafür fehlt jede Grundlage
     * (keine Statusspalte, kein Lock), und eine solche Anzeige wäre geraten.
     */
    // Gibt es zu dieser Nachricht ueberhaupt einen alten Stand?
    //
    // Die Liste kommt aus derselben Auskunft wie die Warteschlange -- die
    // Karte fragt dafuer NICHTS eigenes ab. 227 Karten mit je einer eigenen
    // Abfrage waeren 227 Rundreisen fuer die Frage "gibt es hier einen
    // Knopf".
    hatVerlauf() {
      const ids = this.ratingStatus?.mit_verlauf;
      return Array.isArray(ids) && ids.includes(this.message?.id);
    },

    diffUnklar() {
      return (this.diff?.staende || []).some((s) => s.unklar);
    },

    diffKopfzeile() {
      const v = this.diff?.vergleich;
      if (!v) return "";
      const s = v.summe || {};
      const teile = [
        `${s.geaendert || 0} geändert`,
        `${s.gleich || 0} gleich`,
      ];
      if (s.vorher_ohne_wert) teile.push(`${s.vorher_ohne_wert} vorher ohne Wert`);
      if (s.nachher_ohne_wert) teile.push(`${s.nachher_ohne_wert} jetzt ohne Wert`);
      // ⚠️ Die Kopfzeile zählt NICHT, wie viele Korrekturen ersetzt wurden
      // (Simon, 24.08.: zu negativ). Sie stand hier als einzige Zahl mit
      // einem Werturteil daneben. Je Zeile bleibt der Sachverhalt lesbar:
      // die amber-Färbung und das ausgeschriebene „von Hand" im Vorwert.
      //
      // ⚠️ Ebenfalls NICHT gezählt wird der Herkunftswechsel. Der ist meist
      // harmlos (import → model nach der ersten Bewertung, gemessen an
      // Nachricht 100: 20 von 20 Zeilen) und stünde als Alarm da, der
      // keiner ist.
      return teile.join(" · ");
    },

    warteZustand() {
      const s = this.ratingStatus;
      if (!s) return null;

      const id = this.message?.id;
      const wartet = (s.wartet || []).includes(id);
      const karenz = (s.in_karenz || []).includes(id);
      if (!wartet && !karenz) {
        // ⚠️ „steht in keiner Liste" heißt nur dann „fertig bewertet", wenn
        // die Listen vollständig sind. Der Dienst kappt sie bei 500 und sagt
        // das über `gekappt`; bis zum 18.08. hat das niemand gelesen, und ab
        // der 501. offenen Nachricht behauptete jede Karte das Gegenteil
        // ihres wahren Zustands — schweigend, denn eine fehlende Pille sieht
        // aus wie Ordnung.
        if (!s.gekappt) return null;
        return {
          text: "Zustand unbekannt",
          klasse: "bg-gray-50 text-gray-700 border-gray-200",
          hinweis:
            "Es warten mehr Nachrichten, als die Auskunft aufzählt — sie ist " +
            "bei 500 gekappt. Ob diese hier dazugehört, ist damit nicht zu " +
            "sagen.",
        };
      }

      if (karenz) {
        return {
          text: "Noch zu neu",
          klasse: "bg-gray-50 text-gray-700 border-gray-200",
          hinweis:
            "Der Bewerter nimmt Nachrichten erst nach " +
            (s.karenz_sekunden || 0) +
            " Sekunden auf — diese ist noch jünger.",
        };
      }

      if (s.worker_aktiv === false) {
        return {
          text: "Bewertung abgeschaltet",
          klasse: "bg-amber-50 text-amber-800 border-amber-200",
          hinweis:
            "Diese Nachricht ist unvollständig bewertet, aber der Bewerter " +
            "ist abgeschaltet (disable_rating_worker). Es holt sie niemand ab.",
        };
      }

      return {
        text: "Wartet auf Bewertung",
        klasse: "bg-blue-50 text-blue-800 border-blue-200",
        // ⚠️ Kein getippter Vorgabewert. Nennt die Auskunft den Takt nicht,
        // wird er auch nicht behauptet — eine „10" hier wäre die zweite
        // Wahrheit neben RATING_WORKER_POLL, und sie stünde ausgerechnet
        // dann da, wenn niemand den echten Wert kennt.
        hinweis:
          "Der Bewerter arbeitet die Warteschlange der Reihe nach ab" +
          (s.poll_sekunden
            ? " und fragt alle " + s.poll_sekunden + " Sekunden nach."
            : "."),
      };
    },

    /**
     * Der Vektorzustand dieser Nachricht — sechs Fälle, Reihenfolge bedeutsam.
     *
     * ⚠️ Ohne Auskunft (`ragStatus === null`) fällt die Anzeige auf das
     * Bestandsverhalten zurück: `message.vectorized` ist auch allein eine
     * wahre Aussage, nur eine gröbere. Sie ganz wegzulassen wäre der
     * schlechtere Handel — anders als beim Warteschlangenzustand daneben,
     * der ohne Auskunft gar nichts zu sagen hätte.
     *
     * ⚠️ „Abgeschaltet" sticht die drei Eimer, aber NICHT „unbekannt": eine
     * gekappte Liste sagt über diese Nachricht nichts, auch nicht, dass sie
     * wartet. Und es sticht nur, wenn sie WIRKLICH wartet — ein abgeschalteter
     * Worker ändert an einer fertig vektorisierten Nachricht nichts.
     *
     * ⚠️ „veraltet" und „zurueckgesetzt" bekommen denselben Text. Der
     * Unterschied (steht im Qdrant noch ein alter Punkt?) ist für den
     * Korrektor keine Handlung, sondern eine Eigenschaft des Vektorspeichers
     * — er steht im Titel, nicht in der Pille.
     */
    vektorZustand() {
      if (typeof this.message?.vectorized !== "boolean") return null;

      const s = this.ragStatus;
      const bau = (zustand) => ({
        text: VEKTOR[zustand],
        klasse: VEKTOR_KLASSE[zustand],
        hinweis: vektorHinweis(zustand, s),
      });

      if (!s) return bau(this.message.vectorized ? "vektorisiert" : "offen");

      const id = this.message?.id;
      const nie = (s.nie || []).includes(id);
      const zurueckgesetzt = (s.zurueckgesetzt || []).includes(id);
      const veraltet = (s.veraltet || []).includes(id);
      const wartet = nie || zurueckgesetzt || veraltet;

      if (!wartet) {
        // Steht sie in keinem Eimer, ist das nur dann eine Aussage, wenn die
        // Listen vollständig sind. Dieselbe Lehre wie beim
        // Warteschlangenzustand: eine gekappte Liste sieht aus wie Ordnung.
        if (s.gekappt) return bau("unbekannt");
        return bau(this.message.vectorized ? "vektorisiert" : "offen");
      }

      if (s.worker_aktiv === false) return bau("abgeschaltet");
      if (veraltet || zurueckgesetzt) return bau("veraltet");
      return bau("wartet");
    },

    hasCriteria() {
      return Array.isArray(this.visibleCriterias) && this.visibleCriterias.length > 0;
    },

    normalizedCriteria() {
      const all = Array.isArray(this.allCriterias) ? this.allCriterias : [];
      if (!all.length) return [];

      const ratingsByCriterionId = Object.fromEntries(
          (this.message.criteria_ratings || []).map((mc) => [
            mc.criterion?.id || mc.criterion_id,
            mc,
          ])
      );

      return all.map((c) => {
        const mc =
            ratingsByCriterionId[c.id] || {
              criterion: c,
              criterion_id: c.id,
              count_value: 0,
              value: 0,
              comment: "",
              // Ein Kriterium ohne Bewertungszeile hat keine Herkunft. Ohne
              // die beiden Felder liefe die Provenienz-Anzeige hier ueber
              // undefined statt ueber null -- und "undefined !== 'human'" ist
              // zwar zufaellig richtig, aber aus dem falschen Grund.
              score_source: null,
              model_value: null,
              // Dieselbe Begruendung wie zwei Zeilen darueber: eine Zeile ohne
              // Bewertung hat auch keinen Bewertungszeitpunkt, und null sagt
              // das ausdruecklich. Gerechnet wird die Anzeige ohnehin ueber
              // message.criteria_ratings -- diese Ersatzobjekte tauchen dort
              // gar nicht auf.
              created_at: null,
              updated_at: null,
            };

        const val =
            typeof mc.count_value === "number"
                ? mc.count_value
                : typeof mc.value === "number"
                    ? mc.value
                    : 0;

        return {
          ...mc,
          criterion: mc.criterion || c,
          value: val,
          comment: mc.comment || "",
        };
      });
    },

    visibleCriteriaNormalized() {
      const base = this.normalizedCriteria;

      const vis =
          this.visibleCriterias && this.visibleCriterias.length
              ? new Set(this.visibleCriterias.map((c) => c.id))
              : null;

      if (!vis) return base;
      return base.filter((c) => vis.has(c.criterion.id));
    },

    /**
     * Das Raster der Kriterienpillen.
     *
     * Die feste Untergrenze von 44px war zu klein: gemessen braucht eine Pille
     * mit Zwei-Zeichen-Kuerzel und zweistelligem Wert rund 65px, ein durch
     * Kollisionsaufloesung auf fuenf Zeichen verlaengertes Kuerzel rund 86px.
     * Unterhalb davon quetschte der Flex-Algorithmus das Kuerzel weg -- ab etwa
     * 1366px Fensterbreite war es teilweise, ab 1280px durchgaengig unlesbar.
     *
     * Deshalb zwei Aenderungen:
     * (a) die Untergrenze waechst mit dem laengsten tatsaechlich gezeigten
     *     Kuerzel, statt fuer alle Faelle dieselbe Zahl zu raten, und
     * (b) auto-fit statt einer festen Spaltenzahl -- reicht die Breite nicht
     *     fuer alle Pillen, bricht die Reihe um, statt Inhalt zu verstuemmeln.
     *     Bei wenigen Pillen kollabieren die leeren Spuren, die Reihe sieht
     *     also aus wie vorher.
     */
    criteriaGridStyle() {
      const map = this.criterionAbbrevMap;
      let maxLen = 2;

      for (const mc of this.visibleCriteriaNormalized) {
        const id = mc.criterion?.id ?? mc.criterion_id;
        const kuerzel = map[id] || this.fallbackAbbrev(mc.criterion?.name || "");
        maxLen = Math.max(maxLen, (kuerzel || "").length);
      }

      // 48px tragen Rahmen, Innenabstand, Luecke und den Wertchip; je Zeichen
      // des Kuerzels kommen rund 9px dazu (text-xs, font-semibold).
      const minBreite = 48 + maxLen * 9;

      return {
        display: "grid",
        alignItems: "center",
        gridTemplateColumns: `repeat(auto-fit, minmax(${minBreite}px, 1fr))`,
        columnGap: "0.5rem",
        rowGap: "0.375rem",
      };
    },

    /**
     * Das Nachrichtenfenster waechst, solange an dieser Karte bewertet wird.
     *
     * Gebunden ist es an den OFFENEN EDITOR, nicht an isActive: der Fokus-Modus
     * der Liste haengt an derselben Bedingung (MessagesList:
     * editorOpenForId !== null), und beides auseinanderlaufen zu lassen hiesse,
     * zwei verschiedene Antworten auf "wird hier gerade bewertet" zu haben.
     * Blosses Anspringen einer Karte (j/k) laesst die Liste damit kompakt.
     *
     * ACHTUNG, hier stand eine 150ms-Transition auf max-height, und sie ist
     * ERSATZLOS weg -- nicht aus Geschmack, sondern weil sie holeEditorInsBild
     * unwirksam machte: das Scrollen laeuft im nextTick, die Hoehe wandert
     * danach noch 150ms weiter, und der Editor rutscht dem eben angefahrenen
     * Ziel wieder davon. Live gemessen bei 700px Fensterhoehe: Schnellwahl
     * nach dem Aufklappen weiterhin bei 702..914px, also ausserhalb.
     * Ein Aufklappen ist ohnehin ein Sprung -- der ganze Editor erscheint
     * schlagartig.
     */
    messageTextPaneStyle() {
      return {
        maxHeight: this.activeCriterionId != null ? TEXT_HOEHE_BEWERTUNG : TEXT_HOEHE_KOMPAKT,
      };
    },

    activeCriterion() {
      if (!this.activeCriterionId) return null;
      return this.normalizedCriteria.find((c) => c.criterion.id === this.activeCriterionId) || null;
    },

    authorName() {
      if (!this.message?.user) return "";
      const u = `${this.message.user.first_name || ""} ${this.message.user.last_name || ""}`.trim();
      return u || this.message.user.username || "";
    },

    timestampText() {
      const ts = this.message?.sent_at || this.message?.created_at;
      if (!ts) return "";
      // Der Rueckfall auf den Rohstring bleibt: hier steht der Zeitpunkt als
      // eigene Angabe, und ein unformatierter Wert ist immer noch besser als
      // gar keiner. Fuer die Bewertungszeiten gilt das ausdruecklich NICHT --
      // siehe zeitpunktText.
      return this.zeitpunktText(ts) || ts;
    },

    /**
     * Wann diese Nachricht bewertet wurde, und wann zuletzt auf die Bewertung
     * geschrieben wurde -- beides aus den Zeitstempeln der Bewertungszeilen.
     *
     * Gerechnet wird ueber message.criteria_ratings und NICHT ueber
     * normalizedCriteria: letzteres erfindet fuer jedes unbewertete Kriterium
     * eine Ersatzzeile, deren Zeitstempel es nicht gibt. Die muessten dann
     * eigens wieder herausgefiltert werden -- ein Filter, der genau dann
     * falsch liegt, wenn er gebraucht wird.
     *
     * ACHTUNG, die TOLERANZ ist keine Kosmetik, sie ist am Bestand gemessen
     * (17.08.): von 4540 Bewertungszeilen tragen 1232 ein updated_at, das sich
     * von created_at unterscheidet -- 1190 davon um weniger als eine
     * MILLISEKUNDE. Das ist kein Schreibvorgang, das ist die Anlage selbst:
     * die beiden ORM-Defaults rufen datetime.now() getrennt auf. Nur 42 Zeilen
     * liegen ueber einer Minute, und zwischen 1 ms und 1 min liegt nichts.
     * Ohne Toleranz stuende an 1190 Zeilen "geaendert", wo nie jemand etwas
     * geaendert hat. Eine Sekunde liegt drei Groessenordnungen ueber dem
     * Rauschen und eine unter der kleinsten echten Aenderung.
     */
    bewertungsZeiten() {
      const zeilen = this.message?.criteria_ratings || [];
      const zahl = (w) => {
        if (!w) return null;
        const d = new Date(w);
        return isNaN(d.getTime()) ? null : d.getTime();
      };

      const erstellt = zeilen.map((mc) => zahl(mc.created_at)).filter((t) => t !== null);
      const geaendert = zeilen.map((mc) => zahl(mc.updated_at)).filter((t) => t !== null);

      // Der groesste EIGENabstand einer einzelnen Zelle. Das ist die Groesse,
      // gegen die die Toleranz gemessen wurde -- siehe den Kommentar an
      // ZEITSTEMPEL_TOLERANZ_MS und die ACHTUNG-Zeile unten.
      const eigenabstaende = zeilen
          .map((mc) => {
            const c = zahl(mc.created_at);
            const u = zahl(mc.updated_at);
            return c === null || u === null ? null : u - c;
          })
          .filter((t) => t !== null);

      return {
        // Das AELTESTE created_at. ACHTUNG, die urspruengliche Begruendung
        // ("die Zeilen entstehen in einem Rutsch je Lauf") stimmt NICHT: der
        // RatingWorker ergaenzt fehlende Kriterien nachtraeglich, und der
        // Excel-Import bringt eigene Zeitstempel mit. Im Bestand gemessen
        // (17.08.) spreizt sich created_at bei 31 von 227 Nachrichten ueber
        // bis zu 645 Tage. Das Minimum ist deshalb "seit wann diese Nachricht
        // bewertet ist", nicht "der Zeitpunkt des Laufs" -- der Tooltip sagt
        // das, sobald die Spreizung auffaellt.
        bewertet: erstellt.length ? Math.min(...erstellt) : null,
        // Das JUENGSTE updated_at: irgendeine Zelle ist zuletzt geschrieben
        // worden, und danach wird gefragt.
        geaendert: geaendert.length ? Math.max(...geaendert) : null,
        // ACHTUNG, die teuerste Zeile dieses Blocks. Ob "geaendert" angezeigt
        // wird, haengt HIERAN und nicht an (geaendert - bewertet).
        //
        // Der Unterschied ist am Bestand gemessen und kein Feinschliff: die
        // Toleranz ist ueber den Abstand JE ZELLE erhoben (updated - created
        // derselben Zeile). Gegen die Spanne (max(updated) - min(created))
        // gehalten sagt diese Messung NICHTS -- und die Spanne ist bei 31 von
        // 227 Nachrichten groesser als eine Sekunde, OHNE dass eine einzige
        // Zelle score_source='human' traegt und ohne dass irgendeine Zelle
        // einen Eigenabstand ueber 0,000 s hat. Die Karte haette dort
        // flaechendeckend "geaendert" behauptet, wo nie jemand etwas geaendert
        // hat: die Spanne entsteht allein daraus, dass die Zellen zu
        // verschiedenen Zeiten ANGELEGT wurden.
        maxEigenabstand: eigenabstaende.length ? Math.max(...eigenabstaende) : null,
        // Wie weit die Anlagezeitpunkte auseinanderliegen. Nur fuer den
        // Tooltip -- sie entscheidet nichts.
        anlageSpanne: erstellt.length ? Math.max(...erstellt) - Math.min(...erstellt) : 0,
        zeilen: zeilen.length,
        mitZeit: erstellt.length,
      };
    },

    bewertetText() {
      const z = this.bewertungsZeiten;
      if (z.bewertet === null) return "";
      const text = this.zeitpunktText(z.bewertet);
      return text ? `bewertet ${text}` : "";
    },

    /**
     * Nur, wenn wirklich nachtraeglich geschrieben wurde -- und nur, wenn es
     * einen Bewertungszeitpunkt gibt, gegen den sich das lesen laesst. Ein
     * alleinstehendes "geaendert" saehe aus wie der Bewertungszeitpunkt.
     *
     * ACHTUNG, entschieden wird ueber maxEigenabstand und NICHT ueber
     * (geaendert - bewertet). Die zweite Rechnung sieht naheliegender aus und
     * ist falsch: sie mischt Zellen verschiedener Anlagezeitpunkte und meldet
     * dann eine Aenderung, die nie stattfand. Am Bestand gemessen waeren das
     * 31 von 227 Nachrichten. Die Begruendung steht bei maxEigenabstand.
     */
    geaendertText() {
      const z = this.bewertungsZeiten;
      if (z.bewertet === null || z.geaendert === null) return "";
      if (z.maxEigenabstand === null) return "";
      if (z.maxEigenabstand <= ZEITSTEMPEL_TOLERANZ_MS) return "";
      const text = this.zeitpunktText(z.geaendert);
      return text ? `geändert ${text}` : "";
    },

    /**
     * Woher die Angaben kommen und was sie NICHT heissen. Beides gehoert
     * daneben, weil beides sonst falsch gelesen wird.
     */
    bewertungsZeitTitel() {
      const z = this.bewertungsZeiten;
      const teile = [
        `„Bewertet“ ist das älteste message_criteria.created_at dieser Nachricht `
        + `(${z.mitZeit} von ${z.zeilen} Bewertungszeilen führen einen Zeitstempel).`,
      ];
      if (z.mitZeit < z.zeilen) {
        teile.push(
            "Für die übrigen Zeilen ist kein Zeitpunkt gespeichert; die Angabe "
            + "beschreibt also nur den Teil der Bewertung, den sie gesehen hat.");
      }
      // Die Zellen einer Nachricht entstehen NICHT zwingend in einem Rutsch:
      // der RatingWorker ergaenzt fehlende Kriterien nachtraeglich, der
      // Excel-Import bringt eigene Zeitstempel mit. Wo das passiert ist, darf
      // die eine Zeitangabe nicht so tun, als beschriebe sie einen Lauf.
      if (z.anlageSpanne > ZEITSTEMPEL_TOLERANZ_MS) {
        teile.push(
            "Achtung: die Bewertungszeilen dieser Nachricht sind NICHT zusammen "
            + `entstanden — zwischen der ersten und der letzten liegen `
            + `${this.spanneText(z.anlageSpanne)}. Der Zeitpunkt oben ist der `
            + "Beginn, nicht ein einzelner Lauf.");
      }
      if (this.geaendertText) {
        teile.push(
            "„Geändert“ ist das jüngste updated_at. Es zieht bei JEDEM "
            + "Schreibvorgang auf der Zeile nach — auch bei einem reinen "
            + "Kommentar und beim Excel-Import. Es heißt „zuletzt geschrieben“, "
            + "nicht „korrigiert“; wer Korrekturen zählen will, nimmt die "
            + "Marke „korrigiert“ an den Kriterien.");
      }
      return teile.join(" ");
    },

    normalizedLink() {
      const link = this.message?.link || "";
      if (!link) return "";
      if (/^https?:\/\//i.test(link)) return link;
      return `https://${link}`;
    },

    hasUnsavedChanges() {
      if (!this.activeCriterion) return false;
      return this.editValue !== this.originalValue || this.editComment !== this.originalComment;
    },

    korrigierteAnzahl() {
      return this.visibleCriteriaNormalized.filter((mc) => this.istKorrigiert(mc)).length;
    },

    modellHinweis() {
      // Was im Editor ueber der Bewertung steht -- oder nichts.
      //
      // Die Regel spiegelt model_view aus control_sample.py: der RatingWorker
      // stempelt score_source='model' und laesst model_value LEER (gefuellt
      // wird es erst, wenn ein Mensch den Wert ueberschreibt). Bei 'model' und
      // bei einer Bestandszeile IST der angezeigte Wert also der Modellwert --
      // ein zweiter Wert waere hier eine Erfindung.
      const mc = this.activeCriterion;
      if (!mc || mc.score_source !== "human") return null;
      // Korrigiert, aber ohne gesicherten Modellwert: der Fall nach Shift+0.
      // Es gibt keinen Vorwert, und einen zu behaupten waere schlimmer als zu
      // schweigen.
      if (mc.model_value === null || mc.model_value === undefined) return null;

      const wert = this.formatScore(mc.model_value);
      const marker = mc.model_value_provenance || null;

      if (marker) {
        return {
          text: `Vorwert ${wert} — Herkunft unbelegt`,
          klasse:
              "px-2 py-0.5 rounded text-[0.65rem] bg-amber-50 text-amber-800 border border-amber-200",
          titel:
              marker === "assumed_import"
                  ? "Der Wert stammt aus einer Excel-Datei, nicht aus einem Modelllauf."
                  : "Bestandszeile ohne belegte Herkunft.",
        };
      }

      const herkunft = [mc.model_name, mc.prompt_version_id ? `Prompt-Version ${mc.prompt_version_id}` : null]
          .filter(Boolean)
          .join(", ");

      return {
        text: `Modell schlug ${wert} vor`,
        klasse: "px-2 py-0.5 rounded text-[0.65rem] bg-violet-100 text-violet-700",
        titel: herkunft,
      };
    },

    referencedMessageAuthor() {
      if (!this.message?.referenced_message?.user) return "";
      const u = this.message.referenced_message.user;
      const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
      return name || u.username || `User #${u.id}`;
    },

    truncatedReferencedContent() {
      if (!this.message?.referenced_message?.content) return "";
      const author = this.referencedMessageAuthor;
      const content = this.message.referenced_message.content.trim();
      const prefix = author ? `${author}: ` : "";
      const maxLen = 60;
      const fullText = prefix + content;
      if (fullText.length <= maxLen) return fullText;
      return fullText.substring(0, maxLen) + "...";
    },

    criterionAbbrevMap() {
      const list = this.visibleCriteriaNormalized.map((x) => x.criterion);
      const baseById = new Map();

      for (const c of list) {
        baseById.set(c.id, this.baseAbbrev(c.name));
      }

      const groups = new Map();
      for (const [id, ab] of baseById.entries()) {
        const key = ab || "??";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(id);
      }

      const result = new Map(baseById);

      for (const entry of groups.entries()) {
        const ids = entry[1];
        if (ids.length <= 1) continue;

        const maxLen = 5;
        let len = 3;
        let geloest = false;

        while (len <= maxLen) {
          const used = new Set();
          let allUnique = true;

          for (const id of ids) {
            const name = list.find((c) => c.id === id)?.name || "";
            const candidate = this.prefixClean(name, len);
            if (used.has(candidate)) {
              allUnique = false;
              break;
            }
            used.add(candidate);
          }

          if (allUnique) {
            for (const id of ids) {
              const name = list.find((c) => c.id === id)?.name || "";
              result.set(id, this.prefixClean(name, len));
            }
            geloest = true;
            break;
          }

          len += 1;
        }

        // Letzte Sicherung: bleibt die Gruppe auch bei fuenf Zeichen
        // mehrdeutig, wird durchnummeriert. Vorher fiel die Schleife stumm
        // durch und zwei Pillen trugen dieselbe Beschriftung -- unterscheidbar
        // waren sie dann nur noch ueber den Tooltip.
        if (!geloest) {
          ids.forEach((id, i) => {
            const name = list.find((c) => c.id === id)?.name || "";
            result.set(id, this.prefixClean(name, 3) + (i + 1));
          });
        }
      }

      return Object.fromEntries(result.entries());
    },

    /**
     * Die Belegzeile über dem Verlauf.
     *
     * Das ist die Stelle, an der Beleg und Annahme auseinandergehalten werden.
     * Angezeigt wird immer der Kontext von HEUTE; der einzige harte Beleg über
     * den damaligen Lauf ist rating_meta.conversation_context_count, und das ist
     * eine ANZAHL, kein Wortlaut. Genau so steht es hier auch — "belegt" heißt
     * nie mehr als "die Anzahl stimmt".
     *
     * ACHTUNG, seit dem 17.08.: sie speist AUSSCHLIESSLICH kontextBelegLaut,
     * also nur die Zweige 'differs' und 'inconsistent'. Die Pille, die vorher
     * alle vier Zustände in der Kopfzeile zeigte, ist entfernt (sie hat für
     * Verwirrung gesorgt). Die Zweige 'matches' und der Rückfall 'Kein Beleg'
     * werden damit weiterhin BERECHNET, aber nicht mehr angezeigt -- das ist so
     * entschieden und kein Versehen. Wer sie wieder sichtbar machen will, ändert
     * kontextBelegLaut, nicht diese Funktion.
     */
    /**
     * Was am Relevanzknopf steht.
     *
     * ⚠️ Der laufende Zustand ist kein Schmuck: drüben startet ein Modell,
     * ein Kaltstart kostet gemessen rund 68 s. Ohne sichtbaren Zustand
     * sieht ein laufender Aufruf genauso aus wie ein Knopf, der nicht
     * reagiert hat — und der nächste Klick startet einen zweiten Lauf.
     */
    relevanzKnopfText() {
      if (this.relevanzLaeuft) return this.relevanzLauf.laeuft;
      return this.kontextHatUrteile
          ? this.relevanzLauf.erneut
          : this.relevanzLauf.knopf;
    },

    /** Ob überhaupt ein Urteil an dieser Karte hängt. */
    kontextHatUrteile() {
      const entries = (this.kontext && this.kontext.entries) || [];
      return entries.some((e) => e.relevance);
    },

    /**
     * Was der letzte Lauf offengelassen hat — in einem Satz.
     *
     * ⚠️ Der Grund für diesen Satz: ein Modell, das zwei von fünf Zeilen
     * überspringt, liefert ein Ergebnis, das wie ein vollständiges
     * aussieht. Die übersprungenen Zeilen stehen dann als „?" da, und ohne
     * diesen Hinweis liest sich das wie „noch nicht geprüft".
     *
     * Die Waisen kommen aus der Antwort des Kontextendpunkts, nicht aus dem
     * Lauf: sie entstehen erst, wenn jemand k oder den Cutoff verstellt.
     */
    relevanzHinweis() {
      const teile = [];
      const b = this.relevanzBericht;
      if (b && b.kategorie && !['json_ok', 'partial_json', 'leer'].includes(b.kategorie)) {
        teile.push(`${this.relevanzBefund.unbrauchbar} (${b.kategorie}).`);
      }
      const offen = ((this.kontext && this.kontext.entries) || [])
          .filter((e) => e.relevance && e.relevance.effective === null).length;
      if (offen) teile.push(`${offen} ${this.relevanzBefund.uebersprungen}.`);
      const waisen = (this.kontext && this.kontext.relevance_orphans) || 0;
      if (waisen) teile.push(`${waisen} ${this.relevanzBefund.waisen}.`);
      const ohneId = b && b.ohne_id;
      if (ohneId) teile.push(`${ohneId} ${this.relevanzBefund.ohneId}.`);
      return teile.join(" ");
    },

    kontextBeleg() {
      const k = this.kontext;
      if (!k) return { text: "", klasse: "", titel: "" };

      const heute = k.reconstructed_count;

      if (k.evidence === "matches") {
        return {
          text: `Belegt: der gespeicherte Bewertungslauf sah ebenfalls ${heute} `
              + `Nachricht${heute === 1 ? "" : "en"}. Belegt ist die Anzahl, nicht der Wortlaut.`,
          klasse: "px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200",
          titel: `rating_meta.conversation_context_count = ${k.recorded_count}, `
              + `über ${k.recorded_cells} Zelle(n) einheitlich`,
        };
      }

      if (k.evidence === "differs") {
        return {
          text: `Abweichung: der gespeicherte Lauf sah ${k.recorded_count} `
              + `Nachricht${k.recorded_count === 1 ? "" : "en"}, heute wären es ${heute}. `
              + `Was hier steht, ist NICHT der Kontext jenes Laufs — der Bestand hat sich seither geändert.`,
          klasse: "px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-300 font-semibold",
          titel: `rating_meta.conversation_context_count = ${k.recorded_count} `
              + `über ${k.recorded_cells} Zelle(n)`,
        };
      }

      if (k.evidence === "inconsistent") {
        return {
          text: "Widersprüchlicher Beleg: die Bewertungszellen dieser Nachricht nennen "
              + "untereinander verschiedene Kontextanzahlen. Es gibt keinen einheitlichen "
              + "Beleg, an dem man die Anzeige messen könnte.",
          klasse: "px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-300 font-semibold",
          titel: `${k.recorded_cells} Zelle(n) mit uneinheitlichem `
              + "rating_meta.conversation_context_count",
        };
      }

      return {
        text: "Kein Beleg: für diese Nachricht ist kein Bewertungslauf mit Kontextzähler "
            + "gespeichert. Was hier steht, ist eine Annahme über den heutigen Bestand.",
        klasse: "px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-300",
        titel: "keine Zelle dieser Nachricht führt rating_meta.conversation_context_count",
      };
    },

    /**
     * Ob der Verlauf ausgeklappt ist.
     *
     * Vorgabe: ausgeklappt (Simon, 08.08.). Der Verlauf ist für Kriterien wie
     * "Fachliches Eingehen auf Vorredner" die Beurteilungsgrundlage — wer ihn
     * erst aufklappen muss, bewertet beim ersten Durchgang ohne ihn. Die
     * vorherige Automatik band ihn an den offenen Kriterien-Editor; damit war
     * er beim Lesen der Nachricht noch zu und öffnete sich erst, wenn die
     * Bewertung schon begonnen hatte.
     *
     * Das Zuklappen bleibt: kontextManuell übersteuert (null = der Vorgabe
     * folgen) und wird zurückgesetzt, sobald die Karte nicht mehr die aktive
     * ist — sonst brächte eine vor zwanzig Nachrichten getroffene Auf/Zu-
     * Entscheidung sie mit.
     *
     * ACHTUNG: das gilt nur für die AKTIVE Karte. Der ganze Block hängt an
     * v-if="isActive", und das muss so bleiben — an die Liste gehängt wären es
     * 400+ Abfragen statt einer.
     */
    kontextEntriesSichtbar() {
      if (this.kontextManuell !== null) return this.kontextManuell;
      return true;
    },

    /**
     * Nur die beiden Faelle, in denen die Anzeige in die Irre fuehren kann,
     * bekommen den ausgeschriebenen Satz. Der Rueckfall "kein Beleg" ist mit
     * 163 von 216 Nachrichten der Normalfall und taugt nicht als Warnung.
     *
     * Seit dem 17.08. ist das die EINZIGE Stelle, die kontextBeleg anzeigt --
     * die Kurzform in der Kopfzeile ist entfernt.
     */
    kontextBelegLaut() {
      return ["differs", "inconsistent"].includes(this.kontext?.evidence);
    },

    /**
     * Quelle und Ausbeute in einem Blick: "#dev · 5 von 5", bei einer Antwort
     * "Reply-Kette · 1 von 5 + Bezug".
     *
     * ⚠️ Gezählt wird reconstructed_count, NICHT entries.length. Die Zahl sagt,
     * wie viele Zeilen im Prompt stehen — und die Bezugsnachricht steht dort an
     * einer anderen Stelle. Zählte man die Anzeige, stünde neben einer
     * Kennzahl, die gerade "1 von 5" belegt, die Angabe "2 von 5".
     */
    kontextQuelleKurz() {
      const k = this.kontext;
      if (!k) return "";
      const imPrompt = k.reconstructed_count ?? (k.entries || []).length;
      const zusatz = this.kontextZeigtBezug ? " + Bezug" : "";
      const wieviele = `${imPrompt} von ${k.k}${zusatz}`;
      if (k.source === "reply_chain") return `Reply-Kette · ${wieviele}`;
      // Paket B2-M: die Kette reichte nicht bis k und ist aus dem Kanalfenster
      // aufgefüllt. Ohne diesen Zweig fiele die Zeile auf die nackte Zahl
      // zurück und die Quelle verschwände wortlos — dieselbe Karte, die drei
      // Zeilen tiefer für jede Zeile eine Herkunftsfarbe zeigt, nennte oben
      // keine.
      if (k.source === "mixed") return `Kette + Kanal · ${wieviele}`;
      if (k.source === "channel_window") {
        return this.message.channel ? `#${this.message.channel} · ${wieviele}` : `Kanal · ${wieviele}`;
      }
      return wieviele;
    },

    /** Ob die Liste die direkte Bezugsnachricht mitführt (nur die Anzeige tut das). */
    kontextZeigtBezug() {
      return (this.kontext?.entries || []).some((e) => e.origin === "reply_parent");
    },

    /** Woher das Fenster kommt, und was daran unsicher bleibt. */
    kontextHerkunft() {
      const k = this.kontext;
      if (!k) return "";

      // ACHTUNG, dieser Satz ist keine Höflichkeit. Ohne ihn behauptet die
      // Anzeige still, das Modell habe DAMALS genau diesen Verlauf gesehen —
      // und der ist nirgends persistiert. Er stand bis zum 08.08. sichtbar über
      // der Liste; beim Kürzen der drei Absätze auf eine Zeile ist er
      // ersatzlos verschwunden und war danach nirgends mehr zu lesen.
      const teile = ["So bekäme das Modell den Verlauf heute — die Zeilen unten "
          + "sind aus dem aktuellen Bestand rekonstruiert, nicht mitgeschrieben"];
      if (k.source === "reply_chain") {
        teile.push("Quelle: Reply-Kette");
      } else if (k.source === "mixed") {
        // ⚠️ Ohne diesen Zweig landete eine gemischte Liste im else und die
        // Karte behauptete „Quelle: keine“, während sie darunter fünf Zeilen
        // anzeigt. Das ist keine fehlende Angabe, sondern eine falsche.
        teile.push("Quelle: Reply-Kette, aufgefüllt aus dem Kanalfenster "
            + "(Farbe je Zeile: Kette oder Kanal)");
      } else if (k.source === "channel_window") {
        teile.push(
            this.message.channel
                ? `Quelle: Kanalfenster #${this.message.channel}`
                : "Quelle: Kanalfenster",
        );
      } else {
        teile.push("Quelle: keine");
      }

      teile.push(`k = ${k.k}`);

      if (k.window_saturated) {
        // Nicht verschweigen: das Fenster ist beidseitig auf 4*k Kandidaten je
        // Richtung begrenzt. Ist es ausgeschöpft, kann es jenseits davon
        // weitere Vorgänger geben — die Vollständigkeit ist dann nicht belegbar.
        teile.push("Fenster ausgeschöpft: jenseits davon können weitere Vorgänger liegen");
      }

      // Die Altersgrenze (18.08.). Bis hierher war sie in der Oberfläche
      // ausschließlich im TOTALAUSFALL sichtbar, über reason =
      // all_predecessors_too_old. Fiel ein Teil weg, sah die Karte aus wie ein
      // kurzer Kanal — und genau für diesen Fall ist die Zahl gebaut worden.
      if (k.cutoff_hours > 0) {
        // ⚠️ Der Halbsatz ist keine Höflichkeit. Die Reply-Kette bekommt den
        // Cutoff ausdrücklich NICHT (ein Kettenglied ist ein Beleg, eine
        // Kanalzeile nur eine Annahme aus zeitlicher Nähe). Ohne ihn stünde ein
        // Kettenglied von 2019 unter der Angabe „Höchstalter 24 h“.
        teile.push(`Höchstalter ${k.cutoff_hours} h — gilt nur für Zeilen aus dem `
            + "Kanalfenster, eine Antwortkette wird nicht gekürzt");
      }

      if (k.n_too_old_skipped > 0) {
        // ⚠️ Hier steht bewusst „Kandidaten des Fensters“ und NICHT „fehlende
        // Zeilen“, und der Klammersatz gehört dazu. Die Zahl entsteht in
        // order_window, und zwar VOR dem Filter auf belegte Zeilen und
        // Elternteil (_kandidaten_zum_auffuellen filtert erst danach): auf dem
        // Auffüllweg zählt sie die Kettenglieder mit, die unmittelbar darunter
        // sichtbar in der Liste stehen. Auf dem reinen Kanalweg zählt sie über
        // das ganze Fenster (bis 4*k je Richtung), nicht über die k, die in den
        // Prompt gepasst hätten — order_window verwirft zuerst und kappt erst
        // danach auf k. Als Warnsatz wäre sie deshalb falsch; als Auskunft ist
        // sie richtig, solange dabeisteht, was sie zählt.
        teile.push(k.n_too_old_skipped === 1
            ? "1 Kandidat des Fensters liegt jenseits der Grenze "
              + "(ob er im Prompt fehlt, sagt die Zahl nicht)"
            : `${k.n_too_old_skipped} Kandidaten des Fensters liegen jenseits der `
              + "Grenze (wie viele davon im Prompt fehlen, sagt die Zahl nicht)");
      }

      return teile.join(" · ");
    },

    /** Warum der Prompt keine Verlaufszeile bekommt, falls er keine bekommt. */
    kontextGrund() {
      const k = this.kontext;
      if (!k) return "";

      // ⚠️ Die Bedingung hängt am PROMPT-Zähler, nicht an der Länge der Liste.
      // Seit die Anzeige die Bezugsnachricht mitführt, ist die Liste bei einer
      // Antwort nie mehr leer — an `entries.length` gebunden verschwände genau
      // der Satz, der den häufigsten Fall des Bestands erklärt, und niemand
      // sähe, dass er weg ist.
      const imPrompt = k.reconstructed_count ?? (k.entries || []).length;
      if (imPrompt) return "";

      // Der häufigste Fall im Bestand und der einzige, der ohne Erklärung wie
      // ein Fehler aussieht: eine Reply-Kette der Länge 1. Das direkte
      // Elternteil bleibt bewusst dort, wo es schon steht (in der Reply-Zeile
      // oben) — sonst stünde derselbe Text zweimal im Prompt.
      if (k.source === "reply_chain") {
        // ⚠️ Seit Paket B2-M ist „die Kette hat nur ein Glied“ allein keine
        // vollständige Erklärung mehr: normalerweise würde sie aus dem
        // Kanalfenster aufgefüllt. Steht hier trotzdem nichts, gibt es dafür
        // einen Grund — und der gehört dazu, sonst erklärt die Karte den
        // häufigsten Fall mit einem Satz, der nur noch halb stimmt.
        return "Die Reply-Kette hat nur ein Glied. Die Bezugsnachricht steht bereits oben "
            + "in der Reply-Zeile; das Modell bekommt sie dort, nicht ein zweites Mal hier."
            + (k.reason ? ` (${this.kontextGrundText(k.reason)})` : "")
            + (k.fill_reason
                ? ` Aufgefüllt wurde nicht: ${this.kontextGrundText(k.fill_reason)}`
                : "");
      }

      return this.kontextGrundText(k.reason)
          || "Es gibt keinen Verlauf vor dieser Nachricht.";
    },
  },

  watch: {
    isActive(newVal) {
      if (newVal) {
        window.addEventListener("keydown", this.onKeydown);
        // Der Verlauf ist die Beurteilungsgrundlage, kein Nachschlagewerk --
        // er wird geholt, sobald diese Nachricht die bearbeitete ist.
        this.ladeKontext();
      } else {
        window.removeEventListener("keydown", this.onKeydown);
        // Verlaesst die Karte den Arbeitsplatz, faellt sie auf die Automatik
        // zurueck -- sonst braechte sie eine vor zwanzig Nachrichten getroffene
        // Auf/Zu-Entscheidung mit.
        this.kontextManuell = null;
      }
    },

    hasCriteria(newVal) {
      if (!newVal) this.closeEditor();
    },

    // Der Parent kann den Editor-Zustand nicht sehen, braucht ihn aber fuer den
    // Fokus-Modus. Bewusst zwei schmale Watcher statt eines Objekt-Emits: ein
    // computed ueber activeCriterion haengt an message.criteria_ratings und
    // feuerte damit bei jedem Speichern erneut.
    activeCriterionId(neu) {
      this.$emit("editorState", { messageId: this.message.id, open: neu != null });

      // Zurueck auf drei Zeilen heisst: zurueck auf die ERSTEN drei. Ohne das
      // behaelt der Container seine Scrollposition, und die geschrumpfte
      // Vorschau zeigt einen willkuerlichen Ausschnitt aus der Mitte -- man
      // haelt ihn fuer den Anfang der Nachricht. Beim Aufklappen wird die
      // Position dagegen NICHT angefasst: wer gerade unten liest und dann die
      // erste Pille oeffnet, soll nicht an den Textanfang zurueckgeworfen werden.
      if (neu == null) {
        const el = this.$refs.messageTextPane;
        if (el) el.scrollTop = 0;
      } else {
        this.holeEditorInsBild();
      }
    },

    // Der Index zeigt in die GEFENSTERTE Liste. Blaettert man in ein kuerzeres
    // letztes Fenster, laeuft er sonst ueber.
    visibleCriterias() {
      const n = this.visibleCriteriaNormalized.length;
      if (!n) {
        this.focusedCriterionIndex = 0;
        return;
      }
      if (this.focusedCriterionIndex > n - 1) this.focusedCriterionIndex = n - 1;
      if (this.focusedCriterionIndex < 0) this.focusedCriterionIndex = 0;
    },
  },

  created() {
    // Geladenen Verlauf aus dem Modulspeicher zurueckholen, damit ein
    // Neumounten der Karte keine zweite Abfrage kostet.
    // (Der frueher hier genannte Grund -- das Blaettern im Kriterienfenster
    // mountet die Karte neu -- ist seit dem :key-Fix vom 08.08. weggefallen;
    // ein Filterwechsel baut die Liste aber weiterhin neu auf.)
    const gespeichert = kontextSpeicher.get(this.message.id);
    if (gespeichert) {
      this.kontext = gespeichert.kontext;
      this.kontextFehler = gespeichert.fehler;
    }
  },

  mounted() {
    if (this.isActive) {
      window.addEventListener("keydown", this.onKeydown);
      this.ladeKontext();
    }
    this.istGemerkt = istGemerkt(this.message.id);
    this.merkAbbestellen = abonnieren(() => {
      this.istGemerkt = istGemerkt(this.message.id);
    });
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
    if (this.merkAbbestellen) this.merkAbbestellen();

    // ACHTUNG: ohne diese Abmeldung bleibt der Parent im Fokus-Modus haengen,
    // sobald eine Karte MIT offenem Editor aus visibleMessages faellt --
    // durch Filterwechsel, Loeschen oder Neuladen der Liste. Die ganze Seite
    // waere dann dauerhaft abgedunkelt und stumm.
    if (this.activeCriterionId != null) {
      this.$emit("editorState", { messageId: this.message.id, open: false });
    }
  },

  methods: {
    // --- Bewertungs-Diff -------------------------------------------------

    async toggleDiff() {
      this.diffOffen = !this.diffOffen;
      if (!this.diffOffen || this.diff) return;
      this.diffLaeuft = true;
      this.diffFehler = "";
      try {
        const a = await getRatingHistory(this.message.id);
        this.diff = a?.data || null;
      } catch (e) {
        this.diff = null;
        this.diffFehler = "Der Vergleich konnte nicht geladen werden.";
      } finally {
        this.diffLaeuft = false;
      }
    },

    // Ein Wert, wie er in der Tabelle steht.
    //
    // ⚠️ Eine Zeile ohne Wert zeigt hier eine 0 — und zwar aus EINEM Grund:
    // die Kriterienpille daneben tut es auch. normalizedCriteria gibt einer
    // Zeile ohne Bewertung `value: 0`, und der Fallback darunter macht aus
    // count_value === null ebenfalls eine 0. Stünde im Diff ein Wort, wo die
    // Karte eine Zahl zeigt, behauptete der Vergleich einen Unterschied, den
    // niemand sieht (Simon, 18.08. abends).
    //
    // ⚠️ Der Preis ist eine benannte Grenze des Projekts:
    // die 0 ist dreifach überladen — neutral bewertet,
    // Ausfall-Ersatzwert, nicht bewertet. Am Bestand gemessen fällt das im
    // Diff zusammen: der Importer schreibt 2868 NULL und KEINE EINZIGE 0,
    // das Modell 257-mal 0 und nie NULL. Eine Importzelle ohne Eintrag und
    // eine echte Modell-0 sehen in der Tabelle jetzt gleich aus.
    //
    // ⚠️ Deshalb geht die Auskunft NICHT verloren, sie wandert: die
    // Kopfzeile zählt „n vorher ohne Wert", und der Titel der Zelle
    // (diffHerkunft) sagt es je Zeile. „—" bleibt davon unberührt und heißt
    // weiterhin „es gibt gar keine Zeile".
    //
    // ⚠️ Sichtbar wird ALLEIN die Korrektur, nicht die Herkunft jeder Zelle
    // (Simon, 18.08. abends). Ein „(Import)" oder „(KI)" hinter jeder Zahl
    // stand an 20 von 20 Zeilen und trug damit nichts bei; interessant ist
    // der eine Fall, in dem aus einer Modellzeile eine Menschenzeile
    // geworden ist. Die vollständige Herkunft bleibt im Titel der Zelle.
    diffWert(seite) {
      if (!seite) return "—";
      if (!seite.hat_wert) return "0";
      if (seite.quelle !== "human") return `${seite.wert}`;
      // ⚠️ Die Klammer trägt ZWEI Auskünfte, seit dem 24.08. beide
      // ausgeschrieben (Simon): dass ein Mensch eingegriffen hat („von
      // Hand"), und welchen Wert er überschrieben hat („davor X"). Das bloße
      // „war 1" davor ließ offen, wessen Wert die 1 gewesen ist — und stand
      // in der Vorher-Spalte neben einer zweiten Zahl, die zufällig dieselbe
      // sein kann.
      //
      // ⚠️ „von Hand" ist seit dem 24.08. zugleich der TEXTLICHE Träger der
      // verlorenen Korrektur: die Marke am Kriterium ist entfallen (Simon:
      // zu negativ), und eine verlorene Korrektur heißt per Definition, dass
      // genau diese Seite `human` ist. Ohne das Wort trüge die Aussage
      // allein die Farbe (WCAG 1.4.1).
      //
      // ⚠️ Der Vorwert steht in model_value und wird nie mit dem Wert
      // verrechnet.
      //
      // ⚠️ „davor", nicht „KI": model_value ist der Wert VOR der Korrektur,
      // und der stammt nicht zwingend aus einem Modelllauf. messages.py
      // sichert ihn auch aus einer Import- oder Bestandszeile und markiert
      // das dann in rating_meta.model_value_provenance als assumed_import
      // bzw. assumed_legacy — ein Marker, den diese Auskunft gar nicht
      // bekommt. „KI 2" wäre dort eine Behauptung über eine Vorhersage, die
      // es nie gab. Am Bestand trägt heute keine der 40 gesicherten Zellen
      // einen Marker, stammt also wirklich vom Modell; belegt ist das aber
      // je Zeile nur im Titel (diffHerkunft), nicht hier.
      //
      // ⚠️ Fehlt der Vorwert, sind das ZWEI Lagen und nicht eine — der
      // Unterschied ist am 19.08. aufgefallen, weil an allen drei
      // korrigierten Zellen des Bestands dasselbe stand:
      //
      //   ohne_vorwert = true   vor der Korrektur stand nachweislich keine
      //                         Zahl da. Die Karte hat dem Korrektor dort
      //                         eine 0 gezeigt (normalizedCriteria gibt einer
      //                         Zeile ohne Bewertung value: 0), also ist
      //                         „davor 0" die Auskunft, die zu dem passt, was
      //                         er gesehen hat — dieselbe Entscheidung wie
      //                         beim leeren Zustand der Wertspalten (18.08.).
      //   sonst                 es stand etwas da, aber es ist nicht
      //                         festgehalten (Bestandszeile von vor R1, oder
      //                         eine Korrektur von vor dem 19.08.). Jede Zahl
      //                         wäre hier geraten.
      //                         ⚠️ Am 24.08. nachgemessen: im Bestand traf
      //                         das genau DREI Zellen (#102, #103, #106,
      //                         korrigiert am 17./18.08., also VOR dem
      //                         Marker). Ihnen ist er nachgetragen worden --
      //                         belegt, nicht geraten: eine Importzelle MIT
      //                         Wert hätte seit dem 04.08. zwingend
      //                         model_value und assumed_import bekommen.
      //                         Der Zweig bleibt trotzdem stehen; er ist die
      //                         ehrliche Antwort für jede Zeile ohne Marker.
      //
      // ⚠️ model_value bleibt in beiden Fällen NULL. Die 0 steht in der
      // ANZEIGE, nicht in den Daten; die Wahrheit trägt der Titel der Zelle.
      if (seite.modellwert === null || seite.modellwert === undefined) {
        return seite.ohne_vorwert
            ? `${seite.wert} (von Hand, davor 0)`
            : `${seite.wert} (von Hand, Vorwert nicht festgehalten)`;
      }
      return `${seite.wert} (von Hand, davor ${seite.modellwert})`;
    },

    // Die Langform derselben Herkunft — als Titel an der Zelle.
    //
    // Sie nennt zusätzlich das Modell, und dafür ist in der Zelle kein Platz.
    // Die Kurzform daneben trägt die Aussage allein; dieser Titel ergänzt sie
    // und ist nirgends die einzige Quelle.
    diffHerkunft(seite) {
      if (!seite) return "Zu diesem Kriterium gibt es in diesem Stand keine Zeile.";
      const namen = {
        human: "von Hand korrigiert",
        model: "vom Modell bewertet",
        import: "aus dem Excel-Import übernommen",
      };
      const woher = namen[seite.quelle] || "Herkunft nicht festgehalten";
      let mit = seite.modell ? `${woher} · ${seite.modell}` : woher;
      // ⚠️ DIE tragende Zeile für die Klammer „(von Hand, davor 0)": dort
      // steht eine Zahl, die in den Daten nicht existiert. model_value ist
      // NULL, und
      // was die Karte zeigt, ist die Anzeigekonvention — nicht ein Wert,
      // den jemand vergeben hätte. Ohne diesen Satz wäre die Klammer eine
      // unbelegte Behauptung.
      if (seite.ohne_vorwert) {
        mit += " · vor der Korrektur stand hier keine Zahl — die 0 in Klammern"
            + " ist die Anzeige, wie in der Kriterienpille";
      }
      // ⚠️ Seit die Tabelle für den leeren Zustand eine 0 zeigt, ist DAS
      // hier die einzige Stelle je Zeile, die ihn noch von einer wirklich
      // vergebenen 0 unterscheidet. Die Kopfzeile zählt ihn zwar, aber nur
      // als Summe.
      return seite.hat_wert
          ? mit
          : `${mit} · kein Wert eingetragen — wie in der Kriterienpille als 0 gezeigt`;
    },

    // ⚠️ Nur border-l-*, nie border-* : eine Farbe ohne Seitenangabe färbt
    // alle vier Ränder, und dann entscheidet die Palettenreihenfolge im
    // Stylesheet statt des class-Attributs.
    // ⚠️ Seit dem 24.08. ist die amber-Zeile die EINZIGE Auszeichnung der
    // ersetzten Korrektur, die am Kriterium steht -- die Marke daneben ist
    // entfallen (Simon: zu negativ). Sie steht damit nicht allein: die
    // Vorher-Spalte schreibt „von Hand" aus, denn korrektur_verloren heisst
    // per Definition, dass die alte Zeile `human` war (bewertungsverlauf.py).
    diffZeileKlasse(z) {
      if (z.korrektur_verloren) return "border-l-amber-400 bg-amber-50";
      if (z.status === "gleich") return "border-l-transparent text-gray-600";
      return "border-l-indigo-300 bg-indigo-50 text-gray-900";
    },

    /**
     * Kontextblock auf- und zuklappen; beim ersten Öffnen wird geladen.
     *
     * Nachladen statt Mitliefern: die Liste holt alle 216 Nachrichten in einem
     * Aufruf, und jedes Fenster kostet zwei Abfragen. Das an die Liste zu hängen
     * wären 400+ Abfragen für etwas, das der Korrektor pro Sitzung ein paar Mal
     * ansieht. Gemessen im Container: rund 2 ms je Rekonstruktion.
     */
    async ladeKontext() {
      if (this.kontext || this.kontextLaedt) return;

      this.kontextLaedt = true;
      this.kontextFehler = null;
      try {
        const res = await getConversationContext(this.message.id);
        this.kontext = res.data;
      } catch (e) {
        // Sichtbar scheitern. Ein stiller catch ist in dieser Oberfläche schon
        // einmal teuer geworden: fetchMessages() loggt nur auf die Konsole, und
        // eine gescheiterte Abfrage sieht deshalb aus wie ein leerer Bestand.
        this.kontextFehler =
            "Der Gesprächskontext konnte nicht geladen werden: "
            + (e?.response?.data?.detail || e?.message || "unbekannter Fehler");
      } finally {
        this.kontextLaedt = false;
        this.merkeKontext();

        // ACHTUNG: der Verlauf steht ZWISCHEN Nachricht und Editor und kommt
        // asynchron. Wer eine Pille anklickt, loest beides gleichzeitig aus --
        // das Textfenster waechst sofort, der Verlauf schiebt den Editor
        // Millisekunden spaeter noch einmal nach unten. Live gemessen bei 700px
        // Fensterhoehe: einmaliges Nachfuehren brachte den Editor auf 658..870,
        // also weiterhin 170px unterhalb des Fensters. Ein Nachfuehren ohne das
        // zweite waere ein Nachfuehren auf einen Stand, den es gleich nicht
        // mehr gibt.
        if (this.activeCriterionId != null) this.holeEditorInsBild();
      }
    },

    merkeKontext() {
      kontextSpeicher.set(this.message.id, {
        kontext: this.kontext,
        fehler: this.kontextFehler,
      });
    },

    /**
     * Ein Zeitpunkt in der Schreibweise der Kopfzeile: "14:07 Uhr, 16.08.2026".
     * Nimmt einen ISO-String oder eine Zahl (Millisekunden).
     *
     * ACHTUNG, der Unterschied zu timestampText: ein UNLESBARER Wert gibt hier
     * "" zurueck und nicht den Rohstring. In der Kopfzeile steht der
     * Nachrichtenzeitpunkt als eigene Angabe da, dort ist ein Rohstring besser
     * als nichts; neben dem Wort "bewertet" saehe derselbe Rohstring aus wie
     * ein Zeitpunkt, und die Karte behauptete eine Praezision, die sie nicht
     * hat.
     *
     * Die Formatierung steht bewusst NUR hier: timestampText und die
     * Bewertungszeiten rechnen alle ueber diese Funktion. kontextZeit daneben
     * ist ein anderes Format fuer einen anderen Zweck (Verlaufszeilen, ohne
     * Jahr, mit tabellarischen Ziffern) und bleibt eigenstaendig.
     */
    zeitpunktText(ts) {
      if (ts === null || ts === undefined || ts === "") return "";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return "";
      const time = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
      const date = d.toLocaleDateString("de-DE");
      return `${time} Uhr, ${date}`;
    },

    /**
     * Eine Zeitspanne in Millisekunden als lesbarer Text ("3 Tage", "5 Stunden").
     * Nur für Tooltips -- gerundet, nicht zum Rechnen.
     */
    spanneText(ms) {
      const minuten = Math.round(ms / 60000);
      if (minuten < 60) return `${minuten} Minute${minuten === 1 ? "" : "n"}`;
      const stunden = Math.round(minuten / 60);
      if (stunden < 48) return `${stunden} Stunde${stunden === 1 ? "" : "n"}`;
      const tage = Math.round(stunden / 24);
      return `${tage} Tage`;
    },

    /** Kurze Uhrzeit für eine Kontextzeile. Ohne Zeitstempel ein Strich. */
    kontextZeit(ts) {
      if (!ts) return "—";
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    /**
     * Tooltip einer Sprecherpille im Verlauf.
     *
     * Die Pille zeigt den Klarnamen; hier steht, welche Kennung derselbe
     * Sprecher IM PROMPT trägt. Das ist keine Zierde: die Anzeige und der
     * Prompt sind zwei verschiedene Darstellungen desselben Fensters, und ohne
     * die Kennung ließe sich ein Prompt-Log nicht mehr gegen das halten, was
     * der Korrektor gesehen hat.
     */
    kontextSprecherTitel(e) {
      const eigen = e.author === "author";
      const rolle = eigen
          ? "Derselbe Verfasser wie die bewertete Nachricht"
          : "Anderer Sprecher";
      if (!e.display) {
        // Kein Name aufgelöst — dann steht die Kennung schon in der Pille und
        // hier zu wiederholen wäre nur Lärm.
        return `${rolle}. Kein Nutzername hinterlegt; das Modell sieht „${e.author}“.`;
      }
      return `${rolle}. Im Prompt heißt dieser Sprecher „${e.author}“ — `
          + "das Modell bekommt keinen Klarnamen.";
    },

    /**
     * Der farbige linke Rand = die Herkunft des Eintrags.
     *
     * Warum überhaupt unterschieden wird: „aus der Antwortkette“ ist ein BELEG
     * (jemand hat auf jemanden geantwortet, das steht so in der Nachricht),
     * „aus dem Kanalfenster“ ist eine ANNAHME (zeitlich davor, im selben Kanal
     * — mehr weiß niemand). Bis hierher sahen beide gleich aus, und die Karte
     * behauptete für beide dasselbe.
     *
     * ⚠️ Die Farbe ist die EINZIGE Aussage des Randes. Die Fläche bleibt für
     * das Relevanz-Urteil reserviert; wer hier eine Hintergrundfarbe ergänzt,
     * nimmt sie ihm weg.
     */
    /**
     * Die FLÄCHE einer Verlaufszeile — ihre Relevanz.
     *
     * ⚠️ Rand = Herkunft, Fläche = Relevanz. Die Aufteilung steht seit U3
     * (11.08.) im Template und ist die Bedingung dafür, dass hier zwei
     * verschiedene Aussagen nebeneinander stehen können; ohne sie säßen
     * sie auf demselben Pixel, und eine gewänne zufällig.
     *
     * Kein Urteil heißt KEINE Fläche. Ein grauer Hintergrund für
     * „ungeprüft" sähe aus wie „nicht relevant" — und das ist die
     * Verwechslung, gegen die das ganze Feature gebaut ist.
     */
    kontextRelevanzKlasse(e) {
      return this.RELEVANZ[relevanzZustand(e && e.relevance)].flaeche;
    },

    /** Die Aussage als Text — sie trägt sie, nicht die Farbe. */
    kontextRelevanzText(e) {
      return this.RELEVANZ[relevanzZustand(e && e.relevance)].text;
    },

    /**
     * Der Titel am Schalter — er nennt auch, WER geurteilt hat.
     *
     * ⚠️ Ohne diese Unterscheidung sieht eine Korrektur aus wie ein
     * Modellurteil, und der Datensatz, um dessentwillen erhoben wird, wäre
     * in der Oberfläche nicht mehr ablesbar.
     */
    kontextRelevanzTitel(e) {
      if (e && e.id == null) {
        return this.relevanzBefund.ohneId;
      }
      const rel = e && e.relevance;
      const zustand = this.RELEVANZ[relevanzZustand(rel)];
      // ⚠️ Ohne Urteil sind ZWEI Fälle möglich, seit der Hintergrundlauf
      // existiert: es lief noch nichts, oder es lief und hat diese Zeile
      // ausgelassen. `relevance_ran_at` unterscheidet sie — der Text dazu
      // steht in kontextRelevanz.js, nicht hier.
      if (!rel) return ohneTitel(this.kontext && this.kontext.relevance_ran_at);
      const wer = rel.corrected
          ? `korrigiert${rel.corrected_at ? ", " + this.zeitpunktText(rel.corrected_at) : ""}`
          : `laut ${rel.model_name || "Modell"}`;
      return `${zustand.titel} (${wer})`;
    },

    /**
     * Den Relevanzlauf für diese Nachricht auslösen.
     *
     * Danach wird der Verlauf NEU geholt: die Urteile hängen an den
     * Einträgen der Antwort, nicht am Bericht des Laufs.
     * ⚠️ Dafür muss this.kontext vorher geleert werden — ladeKontext()
     * steigt bei vorhandenem Kontext sofort wieder aus, und die Karte
     * zeigte weiter den Stand von vorher.
     */
    async pruefeRelevanz() {
      if (this.relevanzLaeuft) return;
      this.relevanzLaeuft = true;
      this.relevanzFehler = null;
      try {
        const res = await startContextRelevance(this.message.id);
        this.relevanzBericht = res.data;
        this.kontext = null;
        await this.ladeKontext();
      } catch (e) {
        // Sichtbar scheitern, aus demselben Grund wie bei ladeKontext:
        // ein stiller catch sieht in dieser Oberfläche aus wie ein
        // leeres Ergebnis.
        this.relevanzFehler =
            "Die Relevanzprüfung ist fehlgeschlagen: "
            + (e?.response?.data?.detail || e?.message || "unbekannter Fehler");
      } finally {
        this.relevanzLaeuft = false;
      }
    },

    /**
     * Ein Urteil umschalten: relevant ↔ nicht relevant.
     *
     * Aus „kein Urteil" heraus wird zuerst „relevant" — der Mensch sagt
     * damit ausdrücklich etwas über eine Zeile, zu der das Modell
     * geschwiegen hat.
     */
    schalteRelevanz(e) {
      const gilt = e.relevance ? e.relevance.effective : null;
      this.setzeRelevanz(e, gilt === true ? false : true);
    },

    /**
     * Die Korrektur schreiben.
     *
     * `wert === null` nähme die Korrektur zurück; das Backend kann es
     * weiterhin, die Oberfläche ruft es seit dem 19.08. nicht mehr auf —
     * der Rücknahmeknopf ist entfallen.
     *
     * ⚠️ Das Modellurteil bleibt in jedem Fall stehen — das Backend fasst
     * model_value nicht an, und die Antwort trägt beide Werte zurück.
     * ⚠️ merkeKontext() nicht vergessen: der Modul-Cache ist nicht reaktiv
     * und hielte sonst den Stand vor der Korrektur.
     */
    async setzeRelevanz(e, wert) {
      if (e.id == null) return;
      this.relevanzFehler = null;
      try {
        const res = await setContextRelevance(this.message.id, e.id, wert);
        e.relevance = {
          ...(e.relevance || {}),
          model_value: res.data.model_value,
          human_value: res.data.human_value,
          effective: res.data.effective,
          corrected: res.data.corrected,
          corrected_at: res.data.corrected_at,
        };
        this.merkeKontext();
      } catch (err) {
        this.relevanzFehler =
            "Die Korrektur konnte nicht gespeichert werden: "
            + (err?.response?.data?.detail || err?.message || "unbekannter Fehler");
      }
    },

    kontextHerkunftKlasse(origin) {
      // ⚠️ `border-l-*` und NICHT `border-*`. Live gemessen am 11.08.: mit
      // `border-blue-400` rendert der Rand der Bezugsnachricht GRAU — die
      // Trennlinie oben setzt `border-gray-200`, das färbt alle vier Seiten,
      // und bei gleicher Spezifität entscheidet die Reihenfolge im erzeugten
      // Stylesheet, also die Tailwind-Palettenreihenfolge (blue steht vor
      // gray, indigo dahinter). Der Beleg sah damit aus wie eine Annahme.
      // Dieselbe Falle wie bei BaseButton am 08.08.; die Abhilfe ist, die
      // Eigenschaften gar nicht erst zu überlappen: oben `border-t-gray-200`,
      // links `border-l-<farbe>`.
      if (origin === "reply_parent") return "border-l-blue-400";
      if (origin === "reply_chain") return "border-l-indigo-300";
      if (origin === "channel_window") return "border-l-gray-200";
      // Unbekannt oder fehlend: nicht raten. Ein farbloser Rand hält die
      // Einrückung, ohne eine Herkunft zu behaupten, die niemand kennt.
      return "border-l-transparent";
    },

    /** Derselbe Wert im Klartext — die Farbe allein erklärt sich nicht. */
    kontextHerkunftTitel(origin) {
      if (origin === "reply_parent") {
        return "Die Bezugsnachricht. Sie steht im Prompt an eigener Stelle "
            + "(referenced_message), nicht in dieser Liste — hier steht sie, "
            + "damit der Verlauf am Stück lesbar ist.";
      }
      if (origin === "reply_chain") {
        return "Aus der Antwortkette über der Bezugsnachricht — belegt: "
            + "jemand hat ausdrücklich darauf geantwortet.";
      }
      if (origin === "channel_window") {
        return "Aus dem Zeitfenster desselben Kanals — eine Annahme: zeitlich "
            + "davor und im selben Kanal, mehr ist daran nicht belegt.";
      }
      return "Herkunft nicht angegeben.";
    },

    /**
     * Die Gründe des Backends im Klartext.
     *
     * Die Schlüssel kommen aus conversation_context.py (meta["reason"]). Ein
     * unbekannter Schlüssel wird DURCHGEREICHT und nicht verschluckt — sonst
     * verschwände ein neu eingeführter Grund still aus der Oberfläche.
     */
    kontextGrundText(reason) {
      if (!reason) return "";
      const texte = {
        no_predecessors_in_window:
            "Im Fenster liegt keine Nachricht zeitlich vor dieser.",
        anchor_without_sort_key:
            "Diese Nachricht hat keinen verwertbaren Zeitstempel. Ohne den lässt sich nicht "
            + "sagen, was vor ihr liegt — geraten wird hier nicht.",
        no_channel_id:
            "Die Nachricht ist keinem Channel zugeordnet.",
        k_is_zero:
            "k steht auf 0 (Einstellung rating_context_k bzw. RATING_CONTEXT_K)"
            + " — es ist kein Verlauf konfiguriert.",
        // Seit dem 17.08.: der Verlauf war nicht leer, er ist an der
        // Altersgrenze hängengeblieben. Ohne eigenen Text stünde hier der Satz
        // über das Fenster, und der schickte die Suche in die falsche Richtung.
        all_predecessors_too_old:
            "Alle Vorgänger im Fenster liegen weiter zurück als das eingestellte"
            + " Höchstalter (rating_context_max_age_hours).",
        cycle_detected:
            "Die Reply-Kette verweist im Kreis und wurde abgebrochen.",
        missing_parent:
            "Die Bezugsnachricht ist nicht (mehr) in der Datenbank.",
        parent_in_other_channel:
            "Die Bezugsnachricht liegt in einem anderen Channel; die Kette endet dort.",
        // Paket B2-M: warum die zu kurze Kette NICHT aus dem Kanalfenster
        // aufgefüllt wurde. Ohne diese Texte stünde da „Grund: chain_without_ts“.
        chain_is_full:
            "Die Kette füllt das Fenster bereits aus.",
        chain_without_ts:
            "Ein Glied der Kette hat keinen verwertbaren Zeitstempel. Kette und Kanalfenster "
            + "ließen sich dann nur geraten in eine Reihenfolge bringen — deshalb bleibt es "
            + "bei der Kette allein.",
        no_free_predecessors:
            "Im Kanal liegt keine weitere Nachricht vor dieser, die nicht schon in der Kette steht.",
      };
      return texte[reason] || `Grund: ${reason}`;
    },

    handleApprove() {
      // Frueher blockierte hier ein alert() die Freigabe und liess den Nutzer
      // mit dem Problem allein. Jetzt entscheidet er im Dialog, was mit den
      // Aenderungen passieren soll -- der Parent fuehrt es ueber messageRefs aus.
      if (this.activeCriterion && this.hasUnsavedChanges) {
        this.$emit("approveBlocked", this.message.id);
        return;
      }
      this.$emit("approve", this.message.id);
    },

    // ✅ Exposed to parent (MessagesList) to build reset payload with guards
    getResetAllRatingsPayload() {
      const messageId = this.message?.id;

      if (!messageId) {
        return { ok: false, reason: "No active message id." };
      }

      // (1) block if reviewed
      if (this.message?.reviewed === true) {
        return { ok: false, reason: "This message is reviewed. Use 'Rate again' first." };
      }

      // (4) block if editor has unsaved changes
      if (this.activeCriterion && this.hasUnsavedChanges) {
        return { ok: false, reason: "Unsaved changes in the criterion editor. Save/discard first." };
      }

      // A16: die Nutzlast entsteht aus den Zeilen, die es WIRKLICH gibt.
      // normalizedCriteria läuft über den vollständigen Kriterienkatalog und
      // erfindet für jedes Kriterium ohne Bewertungszeile ein Pseudo-Objekt mit
      // Wert 0. Shift+0 legte damit Zeilen an, die score_source='human' tragen,
      // aber nie einen Modelllauf hatten: ihr Delta count_value - model_value
      // ist nicht berechenbar, und sie fallen über den Filter
      // human_without_model dauerhaft aus der Kontrollstichprobe. Das ist
      // Mess-Schwund, kein Metrik-Fehler.
      const items = (this.message?.criteria_ratings || [])
          .map((mc) => ({
            criterionId: mc.criterion?.id || mc.criterion_id,
            // (2) keep comments unchanged
            comment: mc.comment || "",
          }))
          .filter((x) => x.criterionId != null);

      return { ok: true, messageId, items };
    },

    baseAbbrev(name) {
      const n = (name || "").trim();
      if (!n) return "??";

      // Satzzeichen sind kein Wortanfang. Ohne die Bereinigung ergab
      // "Rollenverstaendnis (eigen)" das Kuerzel "R(" -- und zwar dasselbe wie
      // "Rollenverstaendnis (fremd)". Die Kollisionsaufloesung in
      // criterionAbbrevMap konnte das nicht auffangen, weil ihr Praefixvergleich
      // die Klammern vorher wegwirft und der gemeinsame Praefix
      // "ROLLENVERSTAENDNIS" laenger ist als ihre Obergrenze von fuenf Zeichen.
      // Getrennt wird weiterhin nur an Leerraum, damit Bestandskuerzel wie
      // "FO" fuer "Form/Wortwahl" unveraendert bleiben.
      const parts = n
          .split(/\s+/)
          .map((w) => w.replace(/[^A-Za-z0-9ÄÖÜäöü]/g, ""))
          .filter(Boolean);

      if (parts.length >= 2) {
        const a = parts[0][0] ? parts[0][0].toUpperCase() : "";
        const b = parts[1][0] ? parts[1][0].toUpperCase() : "";
        const ab = `${a}${b}`.trim();
        return ab.length >= 2 ? ab : this.prefixClean(n, 2);
      }

      return this.prefixClean(n, 2);
    },

    prefixClean(name, len) {
      const clean = (name || "")
          .toUpperCase()
          .replace(/[^A-Z0-9ÄÖÜ]/g, "");
      if (!clean) return "??";
      return clean.slice(0, Math.max(2, len));
    },

    fallbackAbbrev(name) {
      return this.baseAbbrev(name);
    },

    focusCommentField() {
      this.$nextTick(() => {
        const el = this.$refs.commentTextarea;
        if (!el) return;
        el.focus();
        const v = el.value || "";
        el.setSelectionRange(v.length, v.length);
      });
    },

    onRateAgain() {
      // ⚠️ Den geholten Vergleich wegwerfen. toggleDiff laedt nur, solange
      // `diff` leer ist, und die Karte wird nach einer Neubewertung nicht neu
      // gebaut (:key ist die message.id). Ohne diese Zeile zeigte genau der
      // Knopf, der die letzte Neubewertung erklaeren soll, die VORLETZTE.
      // Ein Abbruch im Dialog kostet dadurch hoechstens einen erneuten Abruf.
      this.diff = null;
      this.diffFehler = "";
      const id = this.message?.id;
      if (id == null) {
        console.warn("Rate again clicked but message.id is missing", this.message);
        return;
      }
      // Hier wird NICHT mehr nachgefragt. Bis hierher standen zwei Dialoge
      // hintereinander -- dieser (englisch, harmlos formuliert) und danach der
      // ehrliche aus MessagesList. Wer beim ersten bestaetigte, hatte die
      // Konsequenz noch nicht gelesen. Die Entscheidung gehoert dorthin, wo
      // bekannt ist, ob ueberhaupt Bewertungen geloescht werden: in den Parent.
      // Der schliesst nach Erfolg auch den Editor (ueber messageRefs).
      this.$emit("rateAgain", id);
    },

    onDelete() {
      const id = this.message?.id;
      if (id == null) {
        console.warn("Delete clicked but message.id is missing", this.message);
        return;
      }
      // Bestaetigung liegt im Parent, wie bei "Neu bewerten" -- ein Dialogstil
      // fuer alle Entscheidungen dieser Seite.
      this.$emit("delete", id);
    },

    scrollToReferencedMessage() {
      if (!this.message?.referenced_message) return;
      const refMessageId = this.message.referenced_message.id;
      if (this.onScrollToMessage) {
        this.onScrollToMessage(refMessageId);
      }
    },

    /**
     * Merken oder vergessen.
     *
     * ACHTUNG, gemeldet wird nur der FEHLSCHLAG, und zwar über `notice` — die
     * Merkliste ist voll. Ein Erfolg braucht keine Meldung: die Pille wechselt
     * sichtbar die Farbe, und ein Toast bei jedem Klick wäre Lärm. Ohne diesen
     * Zweig bliebe die Karte an der Grenze stumm und der Klick sähe aus, als
     * hätte er nichts getan.
     */
    merkenUmschalten() {
      const vorher = this.istGemerkt;
      const nachher = umschalten(this.message.id);
      this.istGemerkt = nachher;
      if (!vorher && !nachher) {
        // ⚠️ ZWEI Ursachen, und von hier aus sehen sie gleich aus. Bis zum
        // 18.08. stand hier nur die Obergrenze — bei gesperrtem localStorage
        // (privater Modus, volle Quote) meldete die Karte „die Liste ist
        // voll", während gar nichts drinstand.
        this.$emit("notice", {
          text: speicherFehler()
            ? "Der Browser lässt die Merkliste nicht speichern (privater " +
              "Modus oder voller Speicher). Gemerkt wurde nichts."
            : `Die Merkliste ist voll (${MERK_MAX}). Erst etwas vergessen, dann merken.`,
          tone: "info",
        });
      }
    },

    openCriterionEditor(mc, index) {
      if (!this.hasCriteria) return;
      if (this.message?.reviewed) return;

      const criterionId = mc?.criterion?.id ?? mc?.criterion_id;
      if (criterionId == null) return;

      // ACHTUNG: Die Pille traegt @click.stop, damit der Klick nicht zusaetzlich
      // als Kartenklick zaehlt -- dadurch erreichte requestFocus die Liste aber
      // NIE. Wer einen Editor per Klick oeffnete, hatte eine Karte vor sich, die
      // nicht "aktiv" war: der Keydown-Listener haengt an isActive, also tat
      // KEIN Tastenkuerzel etwas, bis man zufaellig woanders in die Karte
      // klickte. Gefunden in der Handprobe vom 08.08.
      this.$emit("requestFocus");

      this.activeCriterionId = criterionId;
      this.editValue = typeof mc.value === "number" ? mc.value : 0;
      this.editComment = mc.comment || "";
      this.originalValue = this.editValue;
      this.originalComment = this.editComment;
      if (typeof index === "number") this.focusedCriterionIndex = index;
    },

    closeEditor() {
      this.activeCriterionId = null;
      this.editValue = null;
      this.editComment = "";
      this.originalValue = null;
      this.originalComment = "";
    },

    discardChanges() {
      this.closeEditor();
    },

    onRatingPickerUpdate(v) {
      this.editValue = this.klemme(v);
    },

    /** Haelt einen Wert in der Skala. Siehe WERT_MIN/WERT_MAX oben. */
    klemme(v) {
      const n = typeof v === "number" && Number.isFinite(v) ? v : 0;
      return Math.max(WERT_MIN, Math.min(WERT_MAX, n));
    },

    setzeWert(v) {
      if (!this.activeCriterion) return;
      this.editValue = this.klemme(v);
    },

    /**
     * Liefert die gedrueckte Ziffer aus e.code, nicht aus e.key.
     *
     * ACHTUNG, das ist der Grund, warum Ctrl+Shift+Ziffer nie funktioniert hat:
     * bei gedrueckter Shift-Taste traegt e.key das SHIFT-ZEICHEN des Layouts
     * (deutsch: ! " § $ % & / ( ) = ), niemals die Ziffer. Ein /^[0-9]$/ auf
     * e.key kann mit Shift also gar nicht treffen. e.code ist layoutfest.
     */
    zifferAus(e) {
      const treffer = /^(?:Digit|Numpad)([0-9])$/.exec(e.code || "");
      return treffer ? Number(treffer[1]) : null;
    },

    saveRating() {
      if (!this.activeCriterion) return;
      this.$emit("rate", {
        messageId: this.message.id,
        criterionId: this.activeCriterion.criterion.id,
        value: this.editValue,
        comment: this.editComment,
      });
      this.originalValue = this.editValue;
      this.originalComment = this.editComment;
    },

    saveAndClose() {
      this.saveRating();
      this.closeEditor();
    },

    scoreClass(value) {
      if (value == null) return "score-unset";
      if (value > 0) return "score-positive";
      if (value < 0) return "score-negative";
      return "score-neutral";
    },

    formatScore(value) {
      if (value == null) return "-";
      return value > 0 ? `+${value}` : `${value}`;
    },

    istKorrigiert(mc) {
      return mc?.score_source === "human";
    },

    focusNextCriterion() {
      const list = this.visibleCriteriaNormalized;
      if (!list.length) return;
      this.focusedCriterionIndex = (this.focusedCriterionIndex + 1) % list.length;
    },

    focusPreviousCriterion() {
      const list = this.visibleCriteriaNormalized;
      if (!list.length) return;
      this.focusedCriterionIndex = (this.focusedCriterionIndex - 1 + list.length) % list.length;
    },

    changeValueBy(delta) {
      if (!this.activeCriterion) return;
      const current = typeof this.editValue === "number" ? this.editValue : 0;
      this.editValue = this.klemme(current + delta);
    },

    /**
     * Ein Schritt zur naechsten bzw. vorigen Bewertungsdimension (Tab/Shift+Tab).
     *
     * Bei offenem Editor wird vorher GESPEICHERT -- aber nur, wenn sich wirklich
     * etwas geaendert hat. Sonst loest jeder Tastendruck beim Durchtabben einen
     * Request aus.
     */
    async schrittZuDimension(delta) {
      const liste = this.visibleCriteriaNormalized;
      if (!liste.length) return;

      const warOffen = !!this.activeCriterion;
      if (warOffen && this.hasUnsavedChanges) this.saveRating();

      const ziel = this.focusedCriterionIndex + delta;

      if (ziel < 0 || ziel >= liste.length) {
        // Fenstergrenze. criteriaWindowStart bleibt Parent-State; die Karte
        // rechnet es NICHT selbst nach, sonst gaebe es zwei Wahrheiten darueber,
        // welches Fenster gerade sichtbar ist.
        this.$emit("requestCriteriaPage", delta > 0 ? "next" : "prev");
        await this.$nextTick();

        const neu = this.visibleCriteriaNormalized;
        if (!neu.length) return;
        // Der Klemm-Watcher oben feuert durch die Prop-Aenderung zuerst; dieses
        // explizite Setzen laeuft danach und gewinnt. Die Reihenfolge ist
        // implizit -- deshalb steht sie hier.
        this.gehZuIndex(delta > 0 ? 0 : neu.length - 1, warOffen);
        return;
      }

      this.gehZuIndex(ziel, warOffen);
    },

    gehZuIndex(idx, editorOeffnen) {
      const mc = this.visibleCriteriaNormalized[idx];
      if (!mc) return;
      this.focusedCriterionIndex = idx;
      if (editorOeffnen) this.openCriterionEditor(mc, idx);
      this.fokussierePille(idx);
    },

    /**
     * Echter DOM-Fokus auf die Kriterienpille.
     *
     * preventScroll ist Pflicht: ohne das zieht der Browser die Pille unter die
     * klebende Kriterienleiste. Das scroll-margin in MessagesList haengt an der
     * KARTE, nicht an der Pille, und greift hier deshalb nicht.
     */
    fokussierePille(idx) {
      this.$nextTick(() => {
        // Bewusst ueber das DOM und nicht ueber $refs: bei v-for sichert Vue die
        // Reihenfolge des Ref-Arrays nicht zu, und die Pillen stecken zusaetzlich
        // im Slot einer Huellkomponente. querySelectorAll liefert
        // Dokumentreihenfolge, und die ist hier die Listenreihenfolge.
        const pillen = this.$el ? this.$el.querySelectorAll("[data-kriterium-pille]") : [];
        const el = pillen[idx];
        if (el && typeof el.focus === "function") el.focus({ preventScroll: true });
      });
    },

    /**
     * Der Tooltip einer Kriterienpille.
     *
     * Die Pille zeigt nur zwei Buchstaben -- ohne den vollen Namen ist sie
     * nicht lesbar. Dazu die Herkunft des Werts, weil genau das die Leistung
     * ist, die R1 sichtbar machen sollte.
     */
    pillenTooltip(mc) {
      const name = mc?.criterion?.name || "Kriterium";
      if (this.message.reviewed) {
        return `${name}\nFreigegeben — „Neu bewerten“ (Kreispfeil rechts) öffnet sie wieder.`;
      }

      const zeilen = [name];
      if (this.istKorrigiert(mc)) {
        if (mc.model_value === null || mc.model_value === undefined) {
          zeilen.push("Von Hand korrigiert (kein Modellwert gesichert)");
        } else if (mc.model_value_provenance) {
          zeilen.push(`Von Hand korrigiert · Vorwert ${this.formatScore(mc.model_value)}, Herkunft unbelegt`);
        } else {
          zeilen.push(`Von Hand korrigiert · Modell schlug ${this.formatScore(mc.model_value)} vor`);
        }
      }
      zeilen.push(`${KUERZEL.editorOeffnen} öffnet · ${KUERZEL.dimensionVor} zur nächsten`);
      return zeilen.join("\n");
    },

    /**
     * Den Editor in den sichtbaren Bereich holen, nachdem das Textfenster
     * gewachsen ist.
     *
     * ACHTUNG, das ist kein Komfort, sondern die Kehrseite des Wachsens -- und
     * sie ist GEMESSEN, nicht vermutet: bei 700px Fensterhoehe stand die
     * Schnellwahl nach dem Aufklappen bei 702..914px, also vollstaendig
     * unterhalb des Fensters. Vorher (Textfenster 62px) lag sie im Bild. Wer
     * mit der Maus bewertet, sieht die Tasten dann nicht mehr, die er gerade
     * gedrueckt hat.
     *
     * block: "nearest" scrollt die KLEINSTMOEGLICHE Strecke und tut gar nichts,
     * wenn der Editor ohnehin sichtbar ist. Das ist wichtig fuer das Durchtabben
     * der 20 Dimensionen: dort laeuft openCriterionEditor bei JEDEM Schritt,
     * und ein "center" wuerde die Seite bei jedem Tastendruck verschieben.
     */
    holeEditorInsBild() {
      this.$nextTick(() => {
        const el = this.$el ? this.$el.querySelector(".criterion-editor") : null;
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ block: "nearest" });
        }
      });
    },

    scrollTextPane(delta) {
      const el = this.$refs.messageTextPane;
      if (!el) return;
      const amount = 40 * delta;
      el.scrollBy({ top: amount, behavior: "smooth" });
    },

    onKeydown(e) {
      if (!this.isActive) return;
      // Ein Dialog liegt oben. Sonst wirkte ein Escape zweimal: es schloesse
      // den Dialog UND verwuerfe im Editor.
      if (this.suspendKeys) return;

      const key = e.key;
      const lowerKey = key.toLowerCase();
      const hasActiveCriterion = !!this.activeCriterion;

      const tag = (e.target && e.target.tagName ? e.target.tagName : "").toLowerCase();
      const isTypingTarget = ["input", "textarea", "select"].includes(tag) || e.target?.isContentEditable;

      // ---- Oberhalb des Tipp-Guards: alles, was auch AUS dem Kommentarfeld
      //      heraus greifen muss.

      if (this.hasCriteria) {
        if (hasActiveCriterion && key === "Enter" && e.shiftKey) {
          this.saveAndClose();
          e.preventDefault();
          return;
        }

        if (hasActiveCriterion && key === "Escape" && e.shiftKey) {
          this.discardChanges();
          e.preventDefault();
          return;
        }

        if (hasActiveCriterion && key === "Escape" && !e.shiftKey) {
          if (this.hasUnsavedChanges) {
            // Vorher passierte hier stumm gar nichts -- der Nutzer drueckte
            // Escape und bekam keinerlei Rueckmeldung.
            this.$emit("notice", {
              text: "Ungespeicherte Änderungen: Shift+Enter speichert, Shift+Esc verwirft.",
              tone: "info",
            });
          } else {
            this.closeEditor();
          }
          e.preventDefault();
          return;
        }
      }

      // Ctrl+Pfeil scrollt den Nachrichtentext. Bleibt bewusst oben: im
      // Textarea hat die Kombination keine native Bedeutung, und beim
      // Kommentieren will man den Text nachlesen koennen.
      if (e.ctrlKey && (key === "ArrowUp" || key === "ArrowDown")) {
        this.scrollTextPane(key === "ArrowDown" ? 1 : -1);
        e.preventDefault();
        return;
      }

      // Ctrl+Ziffer setzt den Wert, auch aus dem Kommentarfeld heraus.
      // ACHTUNG: In Chrome und Edge ist Ctrl+1..8 der Tabwechsel und Ctrl+0 der
      // Zoom-Reset; ob preventDefault dort greift, ist plattformabhaengig.
      // Deshalb ist das nur der Alias -- der verlaessliche Weg sind die blanken
      // Ziffern weiter unten.
      if (hasActiveCriterion && e.ctrlKey) {
        const ziffer = this.zifferAus(e);
        if (ziffer !== null) {
          this.setzeWert(e.shiftKey ? -ziffer : ziffer);
          e.preventDefault();
          return;
        }
      }

      // ---- Ab hier wird nichts mehr gekapert, waehrend jemand tippt.
      if (isTypingTarget) return;

      // Ctrl+A und Ctrl+R stehen JETZT hinter dem Guard. Vorher loeste Ctrl+A
      // die Freigabe aus, waehrend der Cursor im Kommentarfeld stand, und nahm
      // dort "alles markieren" weg.
      if (!hasActiveCriterion && e.ctrlKey && !e.shiftKey && lowerKey === "a") {
        if (!this.message.reviewed) this.handleApprove();
        e.preventDefault();
        return;
      }

      // Ohne Bedingung -- dieselbe Entkopplung wie am Knopf. Ctrl+A darueber
      // BEHAELT seine Schranke: freigeben laesst sich nur, was offen ist.
      if (!hasActiveCriterion && e.ctrlKey && !e.shiftKey && lowerKey === "r") {
        this.onRateAgain();
        e.preventDefault();
        return;
      }

      // Merken. ACHTUNG, die Stelle ist beides Mal knapp gewaehlt und beides Mal
      // absichtlich: UNTER dem Tipp-Guard, damit ein 'm' im Kommentarfeld ein m
      // bleibt -- und UEBER der hasCriteria-Schranke, weil sich gerade die
      // Nachrichten ohne Kriterien zu merken lohnt. Stuende es darunter, waere
      // das Kuerzel genau dort tot, wo man es braucht.
      if (!hasActiveCriterion && !e.ctrlKey && !e.altKey && !e.metaKey
          && lowerKey === "m") {
        this.merkenUmschalten();
        e.preventDefault();
        return;
      }

      if (!this.hasCriteria) return;

      // Tab / Shift+Tab durch die Bewertungsdimensionen.
      if (key === "Tab") {
        // Freigegebene Nachrichten haben deaktivierte Pillen -- dort muss Tab
        // nativ weiterlaufen.
        if (this.message?.reviewed) return;

        // ACHTUNG: ohne diesen Guard kapert die aktive Karte Tab, waehrend der
        // Fokus in der Filterleiste oder auf einem Kopfknopf steht. Die Seite
        // waere dann mit der Tastatur nicht mehr verlassbar.
        const imKartenbereich =
            e.target === document.body || (this.$el && this.$el.contains(e.target));
        if (!imKartenbereich) return;

        e.preventDefault();
        this.schrittZuDimension(e.shiftKey ? -1 : 1);
        return;
      }

      if (hasActiveCriterion && !e.ctrlKey && !e.metaKey && !e.altKey && lowerKey === "c") {
        this.focusCommentField();
        e.preventDefault();
        return;
      }

      const list = this.visibleCriteriaNormalized;

      if (hasActiveCriterion && (key === "ArrowLeft" || key === "ArrowRight")) {
        this.changeValueBy(key === "ArrowRight" ? 1 : -1);
        e.preventDefault();
        return;
      }

      if (!hasActiveCriterion && (key === "ArrowLeft" || key === "ArrowRight")) {
        if (!list.length) return;
        if (key === "ArrowRight") this.focusNextCriterion();
        else this.focusPreviousCriterion();
        this.fokussierePille(this.focusedCriterionIndex);
        e.preventDefault();
        return;
      }

      if (!hasActiveCriterion && key === "Enter") {
        if (this.message?.reviewed) return;
        const mc = list[this.focusedCriterionIndex] || list[0];
        if (mc) this.openCriterionEditor(mc, this.focusedCriterionIndex);
        e.preventDefault();
        return;
      }

      // Blanke Ziffer setzt den Wert, Shift macht ihn negativ. Das ist der
      // verlaessliche Weg -- kein Modifier, den ein Browser fuer sich
      // beansprucht, und ueber e.code layoutfest.
      //
      // Shift+0 ist ausgenommen: -0 waere ohnehin 0, und die Kombination gehoert
      // dem globalen "alle Bewertungen auf 0" der Liste.
      if (hasActiveCriterion && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const ziffer = this.zifferAus(e);
        if (ziffer !== null && ziffer <= WERT_MAX && !(e.shiftKey && ziffer === 0)) {
          this.setzeWert(e.shiftKey ? -ziffer : ziffer);
          e.preventDefault();
          return;
        }
      }
    },
  },
};
</script>
